import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import './App.css';
import MenuDashboard from './components/MenuDashboard';
import PracticeArea from './components/PracticeArea';
import VirtualKeyboard from './components/VirtualKeyboard';
import CookieConsent from './components/CookieConsent';
import ReactGA from 'react-ga4';
import emailjs from '@emailjs/browser';
import { translations } from './Translations'; 
import { supabase } from './supabase';
import { getCourseLessons } from './Data/LessonData';
import { INDIAN_EXAMS } from './Data/ExamData';
import { translateKeystroke } from './Data/KeyboardEngine';
import { PASSAGE_LIBRARY } from './Data/PassageLibrary';
import { motion } from 'framer-motion';
import { initAudio, playKeystroke } from './Data/SoundManager';
import { useLocation, useNavigate } from 'react-router-dom';

// 🚀 LAZY LOAD FOR APP.JSX
const ActiveTypingTest = lazy(() => import('./components/ActiveTypingTest'));

// ==========================================
// 🚀 THE NEW & IMPROVED AUTHENTICATION SCREEN
// ==========================================
function AuthScreen({ isDarkMode, isEmbedded = false }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Handle Standard Email/Password Login
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    if (!email || !password) return alert("Please fill in all required fields.");
    if (isSignUp && (!firstName || !lastName)) return alert("Please provide your full name.");
    if (isSignUp && !agreeTerms) return alert("You must agree to the Terms of Service to create an account.");
    
    setIsLoading(true);

    if (isSignUp) {
      // 🚀 SIGN UP LOGIC
      const { data, error } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          data: {
            full_name: `${firstName} ${lastName}`.trim(),
          }
        }
      });

      if (error) {
        alert(error.message);
      } else {
        // 🚀 THE FIX: The SQL Trigger handles the database now. 
        // We just clear the form and welcome the user!
        setIsSignUp(false); 
        setPassword(""); 
        alert("Account created successfully! You are now logged in.");

        // ==========================================
        // 🚀 1. FIRE GOOGLE ANALYTICS CONVERSION
        // ==========================================
        ReactGA.event({
          category: "Conversion",
          action: "sign_up",
          label: "New Student Registered"
        });

        // ==========================================
        // 🚀 2. FIRE ADMIN EMAIL ALERT
        // ==========================================
        try {
          // We use the 'email' state variable so you know exactly who signed up!
          emailjs.send(
            "service_ktc21vk", 
            "template_fkz191i", 
            {
              user_email: email, 
              message: `🎉 WE GOT ONE! A new user just created an account. Go check Supabase!`
            },
            "KEPtGGP_Ra5MoidNm" 
          );
        } catch (err) {
          console.error("Failed to send admin alert:", err);
        }
      }
      
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        alert("Login failed: " + error.message);
      }
    }
    
    setIsLoading(false);
  };

  // Handle Google Login
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { prompt: 'select_account' }
      }
    });

    if (error) {
      console.error("Google Auth Error:", error.message);
      setIsLoading(false);
    }
  };

  const tStyle = {
    bg: isDarkMode ? '#1e293b' : '#ffffff',
    bodyBg: isDarkMode ? 'transparent' : '#f8fafc',
    text: isDarkMode ? '#f8fafc' : '#0f172a',
    subtext: isDarkMode ? '#94a3b8' : '#64748b',
    border: isDarkMode ? '#334155' : '#cbd5e1',
    inputBg: isDarkMode ? '#0f172a' : '#ffffff',
    btnBg: isDarkMode ? '#38bdf8' : '#0f172a',
    btnText: isDarkMode ? '#0f172a' : '#ffffff',
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', borderRadius: '10px', 
    border: `2px solid ${tStyle.border}`, backgroundColor: tStyle.inputBg, 
    color: tStyle.text, fontSize: '15px', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  };

  const labelStyle = {
    display: 'block', fontSize: '13px', fontWeight: 'bold', color: tStyle.subtext, marginBottom: '6px'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: isEmbedded ? 'auto' : '100vh', width: '100%', backgroundColor: isEmbedded ? 'transparent' : tStyle.bodyBg, fontFamily: 'sans-serif' }}>
      
      <div style={{ flex: 1, display: 'flex', alignItems: isEmbedded ? 'flex-start' : 'center', justifyContent: 'center', width: '100%', boxSizing: 'border-box', padding: isEmbedded ? '0' : '20px' }}>
        {/* 🚀 FIX 1: Increased maxWidth to 550px, reduced vertical padding to 30px */}
        <div style={{ width: '100%', maxWidth: '550px', padding: isEmbedded ? '20px' : '30px', backgroundColor: tStyle.bg, borderRadius: '20px', boxShadow: isDarkMode ? '0 20px 50px rgba(0,0,0,0.5)' : '0 20px 50px rgba(0,0,0,0.08)', border: `1px solid ${tStyle.border}` }}>
          
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '20px' }}> {/* 🚀 FIX 2: Reduced margin */}
            <h2 style={{ color: tStyle.text, margin: '0 0 8px 0', fontSize: '28px', fontWeight: '900' }}>
              {isSignUp ? "Create an Account" : "Login to My Typing Coach"}
            </h2>
            <p style={{ color: tStyle.subtext, margin: 0, fontSize: '15px' }}>
              {isSignUp ? "Start your professional typing journey today" : "Secure login • Continue your typing journey"}
            </p>
          </div>
          
          {/* 🚀 FIX 3: Reduced gap between inputs from 20px to 15px */}
          <form onSubmit={handleEmailAuth} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            
            {/* First & Last Name (Only for Sign Up) */}
            {isSignUp && (
              <div style={{ display: 'flex', gap: '15px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>First Name</label>
                  <input type="text" placeholder="First name" value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = '#38bdf8'} onBlur={e => e.target.style.borderColor = tStyle.border} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Last Name</label>
                  <input type="text" placeholder="Last name" value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = '#38bdf8'} onBlur={e => e.target.style.borderColor = tStyle.border} required />
                </div>
              </div>
            )}

            {/* Email */}
            <div>
              <label style={labelStyle}>Email</label>
              <input type="email" placeholder="Enter email address" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = '#38bdf8'} onBlur={e => e.target.style.borderColor = tStyle.border} required />
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input type={showPassword ? "text" : "password"} placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = '#38bdf8'} onBlur={e => e.target.style.borderColor = tStyle.border} required minLength={6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: tStyle.subtext, cursor: 'pointer', fontSize: '18px' }}>
                  {showPassword ? '👁️' : '🙈'}
                </button>
              </div>
            </div>

            {/* Extras Row (Terms / Forgot Password) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '-5px' }}>
              {isSignUp ? (
                <label style={{ fontSize: '13px', color: tStyle.subtext, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} style={{ cursor: 'pointer' }} />
                  <span>I agree to the <a href="/terms.html" target="_blank" style={{ color: '#38bdf8', textDecoration: 'none' }}>Terms</a> & <a href="/privacy.html" target="_blank" style={{ color: '#38bdf8', textDecoration: 'none' }}>Privacy</a></span>
                </label>
              ) : (
                <div style={{ width: '100%', textAlign: 'right' }}>
                  <span style={{ fontSize: '13px', color: '#38bdf8', cursor: 'pointer', fontWeight: 'bold' }}>Forgot Password?</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button type="submit" disabled={isLoading} style={{ width: '100%', padding: '14px', backgroundColor: tStyle.btnBg, color: tStyle.btnText, border: 'none', borderRadius: '12px', fontSize: '16px', fontWeight: 'bold', cursor: isLoading ? 'not-allowed' : 'pointer', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', transition: 'transform 0.1s' }} onMouseDown={e => !isLoading && (e.currentTarget.style.transform = 'scale(0.98)')} onMouseUp={e => !isLoading && (e.currentTarget.style.transform = 'scale(1)')}>
              {isLoading ? 'Processing...' : (isSignUp ? 'SIGN UP' : 'LOGIN')}
            </button>
          </form>

          {/* Toggle Login/Signup */}
          <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: tStyle.subtext }}>
            {isSignUp ? "Already have an account? " : "Don't have an account? "}
            <span onClick={() => { setIsSignUp(!isSignUp); setEmail(""); setPassword(""); setFirstName(""); setLastName(""); }} style={{ color: '#38bdf8', fontWeight: 'bold', cursor: 'pointer' }}>
              {isSignUp ? "Sign in" : "Create one"}
            </span>
          </div>

          {/* Divider */}
          {/* 🚀 FIX 4: Reduced margin around the divider */}
          <div style={{ display: 'flex', alignItems: 'center', margin: '15px 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: tStyle.border }}></div>
            <span style={{ padding: '0 15px', color: tStyle.subtext, fontSize: '12px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>Or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: tStyle.border }}></div>
          </div>

          {/* Google Button */}
          <button onClick={handleGoogleLogin} disabled={isLoading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', width: '100%', padding: '14px', backgroundColor: 'transparent', color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '12px', fontSize: '15px', fontWeight: 'bold', cursor: isLoading ? 'not-allowed' : 'pointer', transition: 'background-color 0.2s' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = isDarkMode ? '#334155' : '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" style={{ width: '22px', height: '22px' }} />
            Continue with Google
          </button>

          

        </div>
      </div>
    </div>
  );
}

function App() {
  
  const savedLang = localStorage.getItem('appLang');
  const [lang, setLang] = useState(savedLang || 'en');
  const text = translations[lang] || translations['en'];
  const [appState, setAppState] = useState("menu");
  
  const [mistakeHistory, setMistakeHistory] = useState({});
  const [enlargedImage, setEnlargedImage] = useState(null);

  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  useEffect(() => { localStorage.setItem('theme', isDarkMode ? 'dark' : 'light'); }, [isDarkMode]);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const [menuPage, setMenuPage] = useState(1);
  const [currentUser, setCurrentUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

// ==========================================
  // 🔥 THE CLOUD LOGIN HANDLER (Now captures Real Name)
  // ==========================================
  const handleLogin = async (userEmail, fullName) => {
    setCurrentUser(userEmail);
    const finalName = fullName || userEmail.split('@')[0]; // Use real name if available
    
    const newSessionToken = Math.random().toString(36).substring(2, 15);
    localStorage.setItem('activeSessionToken_' + userEmail, newSessionToken);
    
    try {
      let { data, error } = await supabase.from('users').select('*').eq('email', userEmail).single();

      if (!data) {
          const { data: newUser, error: insertError } = await supabase.from('users').insert([
              { 
                email: userEmail, 
                username: finalName, // 🚀 Saves your real name to DB!
                session_token: newSessionToken,
                is_premium: false,
                progress_data: { savedLevel: 0, completedLevels: [], stats: {}, achievements: [] }
              }
          ]).select().single();
          
          if (insertError) console.error("Database insert error:", insertError.message);
          if (!insertError) data = newUser;
      } else {
          // 🚀 Update the username on every login to ensure it stays accurate
          await supabase.from('users').update({ session_token: newSessionToken, username: finalName }).eq('email', userEmail);
      }

      if (data) {
        setIsPremium(data.is_premium);
        if (data.progress_data) {
           const localDb = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
           localDb[userEmail] = data.progress_data;
           localStorage.setItem('typeFasterDB', JSON.stringify(localDb));
           
           // 🚀 THE FIX: Dynamically read the correct saved level based on current layout!
            const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
            const userKey = currentUser || 'Guest';

            if (!db[userKey]) {
                db[userKey] = { savedLevel: 0, completedLevels: [], stats: {}, achievements: [] };
            }

            const layoutSuffix = courseLanguage === 'english' ? '' : `_${courseLanguage}`;
            const compKey = `completedLevels${layoutSuffix}`;
            const statsKey = `stats${layoutSuffix}`;
            const savedKey = `savedLevel${layoutSuffix}`;

            const userCompletedLevels = db[userKey]?.[compKey] || [];
            const userStats = db[userKey]?.[statsKey] || {};
            const statEntries = Object.entries(userStats).filter(([levelIndex]) => !String(levels[Number(levelIndex)]?.title || "").endsWith('.1')).sort((a, b) => Number(a[0]) - Number(b[0]));
        }
      } else {
        setIsPremium(false);
      }
    } catch (err) {
      console.error("Failed to sync user records:", err);
      setIsPremium(false);
    }

    setAppState("menu");
  };

  // ==========================================
  // 🔥 SUPABASE AUTH LISTENER
  // ==========================================
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        if (session?.user?.email) {
          // 🚀 Grab the real Google name from metadata
          const meta = session.user.user_metadata;
          const fullName = meta?.full_name || meta?.name || session.user.email.split('@')[0];
          handleLogin(session.user.email, fullName);
        }
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
      }
    });

    return () => { subscription.unsubscribe(); };
  }, []);

  // ==========================================
  // 🔥 AUTO-REFRESH PREMIUM STATUS WHEN TAB IS FOCUSED
  // ==========================================
  useEffect(() => {
    if (!currentUser) return;

    const checkPremiumStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('is_premium')
          .eq('email', currentUser) 
          .single();

        if (data && data.is_premium) {
          setIsPremium(true); 
        }
      } catch (err) {
        console.error("Error checking premium status:", err);
      }
    };

    checkPremiumStatus();
    window.addEventListener('focus', checkPremiumStatus);
    return () => window.removeEventListener('focus', checkPremiumStatus);
  }, [currentUser]);

  // ==========================================
  // 🚀 REACT ROUTER URL SYNC ENGINE
  // ==========================================
  const location = useLocation();
  const navigate = useNavigate();

  // 1. Read the current tab directly from the URL path (e.g., mytypingcoach.com/exams -> 'exams')
  const menuTab = location.pathname === '/' ? 'home' : location.pathname.substring(1);

  // 2. Override the old state setter to change the URL instead!
  const setMenuTab = (newTab) => {
    // If they log out or hit home, clear the URL to the root
    if (newTab === 'home') {
      navigate('/');
    } else {
      navigate(`/${newTab}`);
    }
    // Make sure we always scroll to the top on page change
    window.scrollTo(0, 0); 
  }; 
  const [statView, setStatView] = useState("speed"); 
  const [soundEnabled, setSoundEnabled] = useState(true);

  const [courseLanguage, setCourseLanguage] = useState(() => {
  return localStorage.getItem('courseLang') || 'english';
  });
  const levels = getCourseLessons(courseLanguage, text);
  
  // 🚀 FIXED: Now properly checks for all Hindi keyboard layouts!
  const isHindiCourse = ['kruti_dev', 'inscript', 'remington_gail', 'remington_cbi'].includes(courseLanguage);
  
  const lessonTitles = isHindiCourse 
    ? [ 
        "Lesson 1: Home Row Core", 
        "Lesson 2: Home Row Extensions", 
        "Lesson 3: Top Row Left", 
        "Lesson 4: Top Row Right", 
        "Lesson 5: Bottom Row Left", 
        "Lesson 6: Bottom Row Right", 
        "Lesson 7: Left Hand (Top & Home) Shifts", 
        "Lesson 8: Left Hand (Bottom) Shifts", 
        "Lesson 9: Right Hand (Top & Home) Shifts", 
        "Lesson 10: Right Hand (Bottom) Shifts", 
        "Lesson 11: The Number Row", 
        "Lesson 12: Symbols & Modifiers", 
        "Lesson 13: Real Passages 1", 
        "Lesson 14: Master Endurance" 
      ]
    : text.lessonTitles;

  const [currentLevel, setCurrentLevel] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); 

  // 🚀 THE FIX: Instantly calculate and load the correct page based on the active keyboard
  useEffect(() => {
    const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
    const userKey = currentUser || 'Guest';
    
    if (db[userKey]) {
      const layoutSuffix = courseLanguage === 'english' ? '' : `_${courseLanguage}`;
      const savedKey = `savedLevel${layoutSuffix}`;
      const compKey = `completedLevels${layoutSuffix}`;
      
      let loadedLevel = db[userKey][savedKey] || 0;
      
      // If they have finished the last saved level, their "Active" level is the next one!
      let activeLevel = loadedLevel;
      if (db[userKey][compKey] && db[userKey][compKey].includes(loadedLevel)) {
          activeLevel += 1;
      }
      
      // Calculate the exact page that contains this active level (6 lessons per page)
      const targetPage = Math.floor(activeLevel / 6) + 1;
      
      setCurrentLevel(activeLevel);
      setMenuPage(targetPage);
    }
  }, [currentUser, courseLanguage]); 
  const [timeLeft, setTimeLeft] = useState(300); 
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [showEarlyExit, setShowEarlyExit] = useState(false);
  const [hasSeenEarlyExit, setHasSeenEarlyExit] = useState(false);
  const [isManualExit, setIsManualExit] = useState(false); 
  const [newlyUnlocked, setNewlyUnlocked] = useState([]); 
  const [activeExam, setActiveExam] = useState(null);
  const [showAltGuide, setShowAltGuide] = useState(false);
  const [examPayload, setExamPayload] = useState(null);
  // 🚀 SEQUENTIAL EXAM STATE MACHINE
  const [seqPhase, setSeqPhase] = useState(0); // 0: Off, 1: English, 2: Rest, 3: Hindi, 4: Results
  const [seqRestTime, setSeqRestTime] = useState(120); // 2 minute break
  const [seqResults, setSeqResults] = useState({ eng: null, hin: null });

  // Rest Timer logic
  useEffect(() => {
    let timer;
    if (appState === 'practice' && seqPhase === 2 && seqRestTime > 0) {
      timer = setInterval(() => setSeqRestTime(p => p - 1), 1000);
    } else if (seqPhase === 2 && seqRestTime === 0) {
      setSeqPhase(3); // Auto-start the Hindi test!
    }
    return () => clearInterval(timer);
  }, [appState, seqPhase, seqRestTime]);

  const openExamInfo = (examId, timeInMinutes, generatedText, selectedLang) => {
    const exam = INDIAN_EXAMS.find(e => e.id === examId) || INDIAN_EXAMS[0];
    setActiveExam(exam);
    
    // 🚀 FIXED: Grab the rules directly from the exam object and save them to the payload!
    setExamPayload({ 
      timeInMinutes, 
      generatedText, 
      selectedLang,
      scoringRule: exam.scoringRule || 'standard',
      defaultBackspace: exam.defaultBackspace || 'free'
    }); 
    
    setAppState("examInfo");
  };
  
  const [difficulty, setDifficulty] = useState("intermediate");
  const goals = {
    beginner: { wpm: 17, accuracy: 90, name: "Beginner" },
    intermediate: { wpm: 25, accuracy: 94, name: "Intermediate" },
    advanced: { wpm: 30, accuracy: 98, name: "Advanced" }
  };
  const [currentGoal, setCurrentGoal] = useState({ wpm: 25, accuracy: 94 });

  const [isTypingTest, setIsTypingTest] = useState(false); 
  const [testMode, setTestMode] = useState("all"); 
  const [testDuration, setTestDuration] = useState(300); 
  const [customText, setCustomText] = useState(""); 
  const [typingLanguage, setTypingLanguage] = useState(courseLanguage);

  const getTypingTestContent = () => {
    let textArray = [];
    if (testMode === "lower") {
      textArray = [
        "the quick brown fox jumps over the lazy dog. typing in lowercase only is a great way to build raw speed and muscle memory without worrying about the shift key. focus on keeping a steady and calm rhythm.",
        "water flows gently down the stream. the birds sing in the early morning light. practice makes perfect when you are learning a new skill. keep your fingers relaxed and let them glide naturally."
      ];
    } else if (testMode === "mixed") {
      textArray = [
        "The quick brown fox jumps over the lazy dog. Typing with capital letters requires you to master the Shift key. This slows down many beginners, but with steady practice, it becomes completely automatic.",
        "Water flows gently down the stream. The birds sing in the early morning light. Practice makes perfect when you are learning a new skill. Keep your fingers relaxed and let them glide naturally."
      ];
    } else if (testMode === "custom") {
       textArray = customText.split('\n').filter(line => line.trim() !== '');
    } else {
      textArray = [
        "The quick brown fox jumps over the lazy dog! In 2026, 100% of advanced typing tests will include numbers like 1, 2, 3, and symbols like @, #, and *!",
        "Email us at test_123@email.com or call (555) 987-6543 for more information. Can you hit 80+ WPM with < 5 mistakes? Good luck & have fun!"
      ];
    }
    return { title: text.freeTest, instruction: text.doYourBest, text: textArray };
  };
  
  const typingTestContent = getTypingTestContent();
  const currentLevelData = isTypingTest ? typingTestContent : levels[currentLevel];
  const levelTitle = isTypingTest ? text.freeTest : currentLevelData?.title || "1.1";

  // 🚀 STEP 2: THE UI INTERCEPTOR FIX
  const isReadingLesson = !isTypingTest && String(levelTitle).endsWith('.1');
  
  // Tell the app it's a paragraph if it ends in .6 OR if we added our new mode flag!
  const isParagraphLesson = !isTypingTest && (String(levelTitle).endsWith('.6') || currentLevelData?.mode === 'paragraph');
  
  const useSplitScreen = isTypingTest || isParagraphLesson;
  
  // Turn OFF the block grid if it is a paragraph lesson
  const useBlockUI = !isTypingTest && !isParagraphLesson && (String(levelTitle).endsWith('.2') || String(levelTitle).endsWith('.3') || String(levelTitle).endsWith('.4'));
  
  // Turn OFF "Stuck Mode" (grid behavior) if it is a paragraph lesson
  const isStuckMode = !isTypingTest && !isParagraphLesson && (String(levelTitle).endsWith('.2') || String(levelTitle).endsWith('.3') || String(levelTitle).endsWith('.4') || String(levelTitle).endsWith('.5'));
  
  const textArray = Array.isArray(currentLevelData?.text) ? currentLevelData.text : [currentLevelData?.text || ""];
  const baseText = textArray[currentPage] || "";
  let textToType = (baseText && useSplitScreen) ? baseText + "↵" : baseText; 
  if (typeof textToType !== 'string') textToType = String(textToType || "");

  const levelInstruction = isTypingTest ? typingTestContent.instruction : currentLevelData?.instruction || "Type the text below.";

  const [userInput, setUserInput] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [keystrokes, setKeystrokes] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [grossWpm, setGrossWpm] = useState(0); 
  const [wpm, setWpm] = useState(0); 
  const [accuracy, setAccuracy] = useState(100);
  const [activeKey, setActiveKey] = useState(null);
  const [missedKeysLog, setMissedKeysLog] = useState({});
  const [targetKeyToHighlight, setTargetKeyToHighlight] = useState(null);
  const [needsShift, setNeedsShift] = useState(false);

  const inputRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const inactivityTimerRef = useRef(null);

  const [currentError, setCurrentError] = useState(false);
  const [errorIndexes, setErrorIndexes] = useState([]);
  const [selectedPassageId, setSelectedPassageId] = useState("random");
  const [passageSource, setPassageSource] = useState("random");

  useEffect(() => {
    if (appState === "practice" && textToType && errorIndexes?.length > 0) {
      setMistakeHistory(prev => {
        const newHistory = { ...prev };
        errorIndexes.forEach(idx => {
          const char = textToType[idx];
          if (char) newHistory[idx] = char === ' ' ? 'SPACE' : char.toUpperCase();
        });
        return newHistory;
      });
    }
  }, [errorIndexes, textToType, appState]);

  useEffect(() => {
    if (appState === "drillInfo" || appState === "menu" || appState === "login" || appState === "languageSelect") {
      setMistakeHistory({});
    }
  }, [appState]);

  useEffect(() => {
    const imagesToPreload = courseLanguage === 'hindi' 
      ? [ "/lesson1hindi.webp", "/lesson2hindi.webp", "/lesson3hindi.webp", "/lesson4hindi.webp", "/lesson5hindi.webp", "/lesson6hindi.webp", "/lesson7hindi.webp", "/lesson8hindi.webp", "/lesson9hindi.webp", "/lesson10hindi.webp" ]
      : [ "/hp-keyboard.webp", "/lesson2.webp", "/lesson3.webp", "/lesson4.webp", "/lesson5.webp", "/lesson6.webp", "/lesson7.webp", "/lesson8.webp", "/lesson9.webp", "/lesson10.webp" ];

    imagesToPreload.forEach((imagePath) => {
      const img = new Image();
      img.src = imagePath;
    });
  }, [courseLanguage]);

  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      setIsTimerRunning(false);
      setIsPaused(true);
    }, 5000);
  };

  const saveProgressToDatabase = async (levelToMarkDone, netWpmScore, grossWpmScore, accuracyScore) => {
    const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
    const userKey = currentUser || 'Guest'; 
    
    if (!db[userKey]) {
        db[userKey] = { savedLevel: 0, completedLevels: [], stats: {}, achievements: [] };
    }

    // 🚀 THE FIX: Save to exact layout suffixes
    const layoutSuffix = courseLanguage === 'english' ? '' : `_${courseLanguage}`;
    const compKey = `completedLevels${layoutSuffix}`;
    const statsKey = `stats${layoutSuffix}`;
    const savedKey = `savedLevel${layoutSuffix}`;

    if (!db[userKey][compKey]) db[userKey][compKey] = [];
    if (!db[userKey][statsKey]) db[userKey][statsKey] = {}; 
    if (!db[userKey].achievements) db[userKey].achievements = []; 

    const existingStat = db[userKey][statsKey][levelToMarkDone];
    if (!existingStat || netWpmScore >= existingStat.netWpm) {
       db[userKey][statsKey][levelToMarkDone] = { netWpm: netWpmScore, grossWpm: grossWpmScore, accuracy: accuracyScore };
    }

    if (!db[userKey][compKey].includes(levelToMarkDone)) {
        db[userKey][compKey].push(levelToMarkDone);
        if (levelToMarkDone >= (db[userKey][savedKey] || 0)) {
            db[userKey][savedKey] = levelToMarkDone;
        }
    }

    let earnedNow = [];
    const checkBadge = (badgeName, icon, condition) => {
      if (condition && !db[userKey].achievements.includes(badgeName)) {
        db[userKey].achievements.push(badgeName);
        earnedNow.push({ name: badgeName, icon: icon });
      }
    };

    checkBadge('First Steps', '🌱', levelToMarkDone === 0);
    checkBadge('Sharpshooter', '🎯', accuracyScore >= 98); 
    checkBadge('Perfectionist', '✨', accuracyScore === 100); 
    checkBadge('Speed Demon', '⚡', netWpmScore >= 30);   
    checkBadge('Keyboard Ninja', '🥷', netWpmScore >= 50);   
    checkBadge('Grandmaster', '👑', netWpmScore >= 70); 
    checkBadge('Endurance Runner', '🏃‍♂️', (levelToMarkDone + 1) % 6 === 0); 
    checkBadge('Dedicated Learner', '🎓', db[userKey].completedLevels.length >= 10);

    if (earnedNow.length > 0) setNewlyUnlocked(earnedNow); 

    localStorage.setItem('typeFasterDB', JSON.stringify(db));
    
    // Only attempt to sync to cloud if they are actually logged in
    try {
      if (currentUser) {
        await supabase
          .from('users')
          .update({ progress_data: db[currentUser] })
          .eq('email', currentUser);
      }
    } catch (err) {
      console.error("Failed to sync to cloud:", err);
    }
  };

  // ==========================================
  // 🔥 EXAM STATS SAVER
  // ==========================================
  const saveExamToDatabase = async (stats) => {
    if (!stats || !currentUser) return;

    const db = JSON.parse(localStorage.getItem('typeFasterDB')) || {};
    
    // Ensure user profile exists
    if (!db[currentUser]) {
      db[currentUser] = { savedLevel: 0, completedLevels: [], stats: {}, achievements: [] };
    }

    // Ensure exam stats object exists
    if (!db[currentUser].examStats) {
      db[currentUser].examStats = { 
        attempts: 0, totalTime: 0, sumGross: 0, sumNet: 0, sumAcc: 0, sumErrorRate: 0 
      };
    }

    // Add current test results to the running totals
    const es = db[currentUser].examStats;
    es.attempts += 1;
    es.totalTime += (stats.time || 0);
    es.sumGross += (stats.grossWpm || 0);
    es.sumNet += (stats.netWpm || 0);
    es.sumAcc += (stats.accuracy || 0);

    // Track strict SSC Error Rate (or default to normal error rate)
    const errRate = stats.examDetails?.errorPercentage 
      ? parseFloat(stats.examDetails.errorPercentage) 
      : Math.max(0, 100 - (stats.accuracy || 100));
    es.sumErrorRate += errRate;

    // Save locally
    localStorage.setItem('typeFasterDB', JSON.stringify(db));
    
    // Sync to cloud
    try {
      await supabase
        .from('users')
        .update({ progress_data: db[currentUser] })
        .eq('email', currentUser);
    } catch (err) {
      console.error("Failed to sync exam stats to cloud:", err);
    }
  };

  const openDrillInfo = (index) => {
    // 🔒 GUEST GUARD: Limit anonymous users to 1 Lesson Drill
    if (!currentUser) {
      const guestDrillCount = parseInt(localStorage.getItem('guest_drill_count') || '0');
      if (guestDrillCount >= 1) {
        alert("🔒 Lesson Limit Reached! To unlock more lessons, please create a free account.");
        setAppState("login"); // 🚀 Forces the screen to switch!
        setMenuTab("login");
        return;
      }
      localStorage.setItem('guest_drill_count', (guestDrillCount + 1).toString());
    }

    setActiveExam(null);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setIsPaused(false);
    if (inputRef.current) inputRef.current.value = "";

    setCurrentLevel(index); setCurrentPage(0); setUserInput(""); 
    setStartTime(null); setGrossWpm(0); setWpm(0); setAccuracy(100); 
    setKeystrokes(0); setMistakes(0); setTimeLeft(300); 
    setIsTimerRunning(false); setShowEarlyExit(false); setHasSeenEarlyExit(false); 
    setIsManualExit(false); setIsTypingTest(false); setCurrentError(false); setErrorIndexes([]);
    
    setTypingLanguage(courseLanguage); 
    
    if (String(levels[index]?.title).endsWith('.1')) setAppState("practice"); 
    else setAppState("drillInfo"); 
  };

  const startTypingTest = (overrideMode = null, overrideText = null, overrideTime = null, overrideLang = 'english') => {
    // 🔒 GUEST GUARD: Limit anonymous users to 1 Demo Test
    if (!currentUser) {
      const hasTakenDemo = localStorage.getItem('guest_demo_taken');
      if (hasTakenDemo === 'true') {
        alert("🔒 Demo Limit Reached! Please create a free account to continue practicing and tracking your speed.");
        setAppState("login"); // 🚀 Forces the screen to switch!
        setMenuTab("login");
        return;
      }
      // Punch the ticket instantly
      localStorage.setItem('guest_demo_taken', 'true');
    }

    const finalMode = typeof overrideMode === 'string' ? overrideMode : testMode;
    const finalText = typeof overrideText !== 'undefined' && overrideText !== null ? overrideText : customText;
    const finalTime = typeof overrideTime === 'number' ? overrideTime : testDuration;

    if (finalMode === "custom" && typeof finalText === 'string' && !finalText.trim()) {
      return alert("Please paste text first!");
    }

    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setIsPaused(false);
    if (inputRef.current) inputRef.current.value = "";

    if (typeof overrideMode === 'string') setTestMode(overrideMode);
    if (typeof overrideText === 'string') setCustomText(overrideText);
    if (typeof overrideTime === 'number') setTestDuration(overrideTime);
    
    setTypingLanguage(overrideLang || courseLanguage);

    setIsTypingTest(true); 
    setCurrentPage(0); 
    setUserInput(""); 
    setStartTime(null); 
    setGrossWpm(0); 
    setWpm(0); 
    setAccuracy(100); 
    setKeystrokes(0); 
    setMistakes(0); 
    setTimeLeft(finalTime); 
    setIsTimerRunning(false); 
    setShowEarlyExit(false); 
    setHasSeenEarlyExit(false); 
    setIsManualExit(false); 
    setCurrentError(false); 
    setErrorIndexes([]);
    setAppState("practice"); 
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const beginPractice = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    setIsPaused(false);
    setAppState("practice");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    const handleSpaceToStart = (e) => {
      // 🚀 Prevents Spacebar from triggering if user is typing in the "Paste Text" box!
      const targetTag = e.target.tagName.toLowerCase();
      if (targetTag === 'textarea' || targetTag === 'input' || targetTag === 'select') return;

      if (appState === "drillInfo" && e.code === "Space") {
        e.preventDefault(); 
        beginPractice();
      } else if (appState === "examInfo" && e.code === "Space") {
        e.preventDefault(); 
        // 🚀 FIX: Instead of bypassing the logic, we force the Spacebar to click the green Start button!
        document.getElementById('btn-ok')?.click(); 
      }
    };
    window.addEventListener('keydown', handleSpaceToStart);
    return () => window.removeEventListener('keydown', handleSpaceToStart);
  }, [appState]);

  useEffect(() => {
    if (keystrokes > 0) {
      const startingTime = isTypingTest ? testDuration : 300;
      const timeElapsedInSeconds = startingTime - timeLeft; 
      const timeElapsedInMinutes = timeElapsedInSeconds > 0 ? (timeElapsedInSeconds / 60) : 0.0166; 
      
      const calculatedGrossWpm = Math.max(0, Math.round((keystrokes / 5) / timeElapsedInMinutes));
      setGrossWpm(calculatedGrossWpm);

      const calculatedNetWpm = Math.max(0, Math.round(calculatedGrossWpm - (mistakes / timeElapsedInMinutes)));
      setWpm(calculatedNetWpm);

      const calculatedAccuracy = Math.max(0, Math.round(((keystrokes - mistakes) / keystrokes) * 100));
      setAccuracy(calculatedAccuracy);
    }
  }, [keystrokes, mistakes, timeLeft, isTypingTest, testDuration]);

  useEffect(() => {
    let interval = null;
    if (isTimerRunning && timeLeft > 0) {
      interval = setInterval(() => { setTimeLeft(prev => prev - 1); }, 1000);
    } else if (timeLeft === 0 && appState === "practice") {
      setIsTimerRunning(false); 
      setAppState("results");
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timeLeft, appState, isPremium]);

  useEffect(() => {
    if (appState === "practice" && !isTypingTest && isTimerRunning) {
      if (timeLeft <= 120 && timeLeft > 0) {
        if (wpm >= currentGoal.wpm && accuracy >= currentGoal.accuracy) {
          if (!hasSeenEarlyExit) {
            setIsTimerRunning(false);
            setShowEarlyExit(true);
            setHasSeenEarlyExit(true);
          }
        }
      }
    }
  }, [timeLeft, wpm, accuracy, appState, isTypingTest, isTimerRunning, currentGoal, hasSeenEarlyExit]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (appState === 'practice') {
          setIsTimerRunning(false);
          setShowEarlyExit(false); 
          setAppState('menu');
        } else if (appState === 'drillInfo' || appState === 'results') {
          setAppState('menu');
        } else if (appState === 'menu') {
          setCurrentUser(null);
          setAppState('login');
        }
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [appState]);

  useEffect(() => {
    const handleExitChoice = (e) => {
      if (showEarlyExit) {
        if (e.key === 'Enter') {
         e.preventDefault(); e.stopPropagation();
         setIsTimerRunning(false); setShowEarlyExit(false); 
         setAppState("results");
        } else if (e.code === 'Space' || e.key === ' ') {
          e.preventDefault(); e.stopPropagation();
          setShowEarlyExit(false); setIsTimerRunning(true); 
          setTimeout(() => inputRef.current?.focus(), 0);
        }
      }
    };
    window.addEventListener('keydown', handleExitChoice, { capture: true });
    return () => window.removeEventListener('keydown', handleExitChoice, { capture: true });
  }, [showEarlyExit, isPremium]);

  // 🚨 THE MULTI-DEVICE SECURITY WATCHDOG 🚨
  useEffect(() => {
    let watchdogInterval;
    if (currentUser && appState !== "login" && appState !== "languageSelect") {
      watchdogInterval = setInterval(async () => {
        try {
          const myLocalToken = localStorage.getItem('activeSessionToken_' + currentUser);
          const { data, error } = await supabase
            .from('users')
            .select('session_token')
            .eq('email', currentUser) 
            .single();

          if (data && data.session_token !== myLocalToken) {
            alert("⚠️ SECURITY ALERT: Your account was just accessed from another device. You have been logged out.");
            setCurrentUser(null);
            setAppState("login");
          }
        } catch (err) {
          console.error("Watchdog failed to verify session:", err);
        }
      }, 10000); 
    }
    return () => clearInterval(watchdogInterval);
  }, [currentUser, appState]);
  
  // ==========================================
  // 🔥 100% IME-SAFE TYPING HANDLER (Learning Mode)
  // ==========================================
  const processTyping = (typedText) => {
    if (typedText.length > textToType.length) {
      if (inputRef.current) inputRef.current.value = userInput;
      return;
    }

    if (startTime === null && typedText.length > 0) setStartTime(Date.now()); 
    if (!isTimerRunning && typedText.length > 0) { setIsTimerRunning(true); setIsPaused(false); }

    let commonLen = 0;
    while (commonLen < userInput.length && commonLen < typedText.length && userInput[commonLen] === typedText[commonLen]) {
        commonLen++;
    }

    let currentStuck = false;
    let finalOutput = userInput.slice(0, commonLen);

    if (commonLen < userInput.length) {
        if (isStuckMode) {
            if (inputRef.current) inputRef.current.value = userInput;
            return;
        }
        
        if (isParagraphLesson || isTypingTest) {
            let mistakesRecovered = 0;
            for (let i = commonLen; i < userInput.length; i++) {
                if (userInput[i] !== textToType[i]) mistakesRecovered++;
            }
            if (mistakesRecovered > 0) {
                setMistakes(prev => Math.max(0, prev - mistakesRecovered));
                setErrorIndexes(prev => prev.filter(idx => idx < commonLen));
                setMistakeHistory(prev => {
                    const newHistory = { ...prev };
                    for (let i = commonLen; i < userInput.length; i++) delete newHistory[i];
                    return newHistory;
                });
            }
        }
    }

    let newMistakes = 0;
    if (commonLen < typedText.length) {
        for (let i = commonLen; i < typedText.length; i++) {
            const newChar = typedText[i];
            const expectedChar = textToType[i];
            setKeystrokes(prev => prev + 1);

            if (newChar === expectedChar) {
                finalOutput += newChar;
            } else {
                newMistakes++;
                const missedLetter = expectedChar === '↵' ? 'ENTER' : (expectedChar ? expectedChar.toUpperCase() : 'UNKNOWN');
                setMissedKeysLog(prev => ({ ...prev, [missedLetter]: (prev[missedLetter] || 0) + 1 }));
                setErrorIndexes(prev => prev.includes(i) ? prev : [...prev, i]);
                
                if (isStuckMode) {
                    currentStuck = true;
                    break;
                } else {
                    finalOutput += newChar;
                }
            }
        }
    }

    if (newMistakes > 0) {
        if (soundEnabled) { initAudio(); playKeystroke('error'); }
        setMistakes(prev => prev + newMistakes);
        if (currentStuck) {
            setCurrentError(true);
            if (inputRef.current) inputRef.current.value = finalOutput;
        }
    } else if (typedText.length !== userInput.length) {
        if (soundEnabled) { initAudio(); playKeystroke('standard'); }
        setCurrentError(false);
    }

    setUserInput(finalOutput);
    if (inputRef.current && inputRef.current.value !== finalOutput) {
        inputRef.current.value = finalOutput; // Keep DOM input in sync
    }
    resetInactivityTimer();

    if (finalOutput.length === textToType.length && !currentStuck) {
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        
        // 🚀 MARK GUEST PROGRESS BEFORE RESETTING
        if (!currentUser) {
          if (isTypingTest) {
            localStorage.setItem('guest_demo_taken', 'true');
          } else {
            const currentCount = parseInt(localStorage.getItem('guest_drill_count') || '0');
            localStorage.setItem('guest_drill_count', (currentCount + 1).toString());
          }
        }

        // NEW: End the test immediately if it's an untimed mode!
        if (isTypingTest && testDuration === 3600) {
            setIsTimerRunning(false);
            setAppState("results");
            return;
        }

        setTimeout(() => { 
            if (inputRef.current) inputRef.current.value = ""; 
            setUserInput(""); setCurrentPage(prev => (prev + 1) % textArray.length); 
            setCurrentError(false); setErrorIndexes([]);
            resetInactivityTimer(); 
        }, 150); 
    }
  };

  const handleTyping = (event) => {
    if (appState !== "practice" || showEarlyExit) return;
    
    processTyping(event.target.value);
  };

 const handleKeyDown = (e) => {
    if (e.nativeEvent && e.nativeEvent.isComposing) return;

    setActiveKey(e.key.toLowerCase());

    // 🚀 THE FIX: STRICT BACKSPACE LOCK FOR SPLIT SCREEN
    if (e.key === 'Backspace' && (isTypingTest || isParagraphLesson)) {
        if (userInput.endsWith(' ')) {
            e.preventDefault();
            if (soundEnabled) { initAudio(); playKeystroke('error'); }
            return;
        }
    }

    // 🚀 NEW FIX: SPACEBAR WORD-JUMPING LOGIC!
    if (e.key === ' ' && (isTypingTest || isParagraphLesson)) {
        const expectedChar = textToType[userInput.length];
        
        // If they hit space in the middle of a word (not at a natural space or enter key)
        if (expectedChar && expectedChar !== ' ' && expectedChar !== '↵') {
            e.preventDefault(); // Stop the standard blank space from typing
            
            // Find where this current word actually ends
            let nextSpaceIndex = textToType.indexOf(' ', userInput.length);
            let nextEnterIndex = textToType.indexOf('↵', userInput.length);
            
            // Target the closest word boundary
            let targetIndex = textToType.length;
            if (nextSpaceIndex !== -1 && nextEnterIndex !== -1) targetIndex = Math.min(nextSpaceIndex, nextEnterIndex);
            else if (nextSpaceIndex !== -1) targetIndex = nextSpaceIndex;
            else if (nextEnterIndex !== -1) targetIndex = nextEnterIndex;

            if (targetIndex <= textToType.length) {
                const skippedCount = targetIndex - userInput.length;
                
                // 1. Mark the skipped letters as mistakes
                setMistakes(prev => prev + skippedCount);
                setKeystrokes(prev => prev + skippedCount + 1); // +1 for the spacebar press
                
                // 2. Turn the skipped letters red
                const newErrors = [];
                for(let i = userInput.length; i < targetIndex; i++) newErrors.push(i);
                setErrorIndexes(prev => [...new Set([...prev, ...newErrors])]);
                
                if (soundEnabled) { initAudio(); playKeystroke('error'); }

                // 3. Jump the input forward to the next word!
                let nextInput = userInput + ' '.repeat(skippedCount);
                if (textToType[targetIndex] === ' ') nextInput += ' '; // Add the space separator
                
                setUserInput(nextInput);
                if (inputRef.current) inputRef.current.value = nextInput;
                
                // Start the timer if this was their very first keypress
                if (!isTimerRunning && !isPaused) { 
                    setIsTimerRunning(true);
                    resetInactivityTimer();
                }
                return; // Stop further key logic
            }
        }
    }

    // 🚀 THE MAGIC ENGINE INTERCEPTOR
    if (appState === 'practice' && !showEarlyExit && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
      if (typingLanguage === 'inscript' || typingLanguage === 'remington_gail' || typingLanguage === 'remington_cbi') {
        e.preventDefault(); 
        
        const processedChar = translateKeystroke(e.key, typingLanguage);
        
        if (processedChar !== '') {
          processTyping(userInput + processedChar);
        }
        return; 
      }
    }

    if (e.key === ' ') {
      if (appState === 'practice' && !isTimerRunning && !isPaused) { 
         setIsTimerRunning(true);
         resetInactivityTimer();
      }
      else if (appState === 'practice' && showEarlyExit) { e.preventDefault(); setShowEarlyExit(false); setIsTimerRunning(true); setTimeout(() => inputRef.current?.focus(), 0); }
    }
    
    if (e.key === 'Enter' && showEarlyExit) {
      setIsTimerRunning(false); setShowEarlyExit(false); setAppState("results");
      return;
    }

    if (e.key === 'Enter' && appState === 'practice' && !showEarlyExit) {
      const expectedChar = textToType[userInput.length];

      if (expectedChar === '↵') {
        e.preventDefault(); 
        if (!isTimerRunning) { setIsTimerRunning(true); setIsPaused(false); }
        setKeystrokes(prev => prev + 1);

        if (soundEnabled) { initAudio(); playKeystroke('enter'); }
        setCurrentError(false);
        const newTypedText = userInput + '↵';
        setUserInput(newTypedText);
        if (inputRef.current) inputRef.current.value = newTypedText; 
        
        if (newTypedText.length === textToType.length) {
          if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
          
          if (isTypingTest && testDuration === 3600) {
              setIsTimerRunning(false);
              setAppState("results");
              return;
          }

          setTimeout(() => {
            if (inputRef.current) inputRef.current.value = ""; 
            setUserInput(""); setCurrentPage(prev => (prev + 1) % textArray.length);
            setCurrentError(false); setErrorIndexes([]);
            resetInactivityTimer(); 
          }, 150);
        } else {
          resetInactivityTimer();
        }
      } else {
        e.preventDefault();
        if (!isTimerRunning) { setIsTimerRunning(true); setIsPaused(false); }
        if (soundEnabled) { initAudio(); playKeystroke('error'); } 
        setMistakes(prev => prev + 1);
        const missedLetter = 'ENTER';
        setMissedKeysLog(prev => ({ ...prev, [missedLetter]: (prev[missedLetter] || 0) + 1 }));
        setErrorIndexes(prev => prev.includes(userInput.length) ? prev : [...prev, userInput.length]);
        
        if (isStuckMode) {
           setCurrentError(true);
           resetInactivityTimer();
           return;
        } else {
           const newTypedText = userInput + ' '; 
           setUserInput(newTypedText);
           if (inputRef.current) inputRef.current.value = newTypedText; 
           if (newTypedText.length === textToType.length) {
             if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
             setTimeout(() => { 
               if (inputRef.current) inputRef.current.value = ""; 
               setUserInput(""); setCurrentPage(prev => (prev + 1) % textArray.length); 
               setCurrentError(false); setErrorIndexes([]);
               resetInactivityTimer(); 
             }, 150);
           } else {
             resetInactivityTimer();
           }
        }
      }
    }
  };

  const handleKeyUp = () => setActiveKey(null);

  if (appState === "languageSelect") {
    const isDark = isDarkMode;
    const btnStyle = { padding: '15px 30px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '12px', border: 'none', color: 'white', flex: '1 1 200px', maxWidth: '250px', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', transition: 'transform 0.1s' };
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', width: '100%', backgroundColor: isDark ? '#0f172a' : '#f1f5f9', fontFamily: 'sans-serif', padding: '20px', boxSizing: 'border-box' }}>
        <h1 style={{ color: isDark ? '#38bdf8' : '#0284c7', marginBottom: '30px', textAlign: 'center' }}>🌍 Choose Your Language</h1>
        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '600px' }}>
          <button onClick={() => { setLang('en'); localStorage.setItem('appLang', 'en'); setAppState('login'); }} style={{ ...btnStyle, backgroundColor: '#eab308' }} onMouseDown={e=>e.target.style.transform='scale(0.95)'} onMouseUp={e=>e.target.style.transform='scale(1)'}>🇺🇸 English</button>
          <button onClick={() => { setLang('hi'); localStorage.setItem('appLang', 'hi'); setAppState('login'); }} style={{ ...btnStyle, backgroundColor: '#10b981' }} onMouseDown={e=>e.target.style.transform='scale(0.95)'} onMouseUp={e=>e.target.style.transform='scale(1)'}>🇮🇳 हिन्दी (Hindi)</button>
          <button onClick={() => { setLang('bn'); localStorage.setItem('appLang', 'bn'); setAppState('login'); }} style={{ ...btnStyle, backgroundColor: '#8b5cf6' }} onMouseDown={e=>e.target.style.transform='scale(0.95)'} onMouseUp={e=>e.target.style.transform='scale(1)'}>🇧🇩 বাংলা (Bengali)</button>
          <button onClick={() => { setLang('es'); localStorage.setItem('appLang', 'es'); setAppState('login'); }} style={{ ...btnStyle, backgroundColor: '#f43f5e' }} onMouseDown={e=>e.target.style.transform='scale(0.95)'} onMouseUp={e=>e.target.style.transform='scale(1)'}>🇪🇸 Español (Spanish)</button>
          <button onClick={() => { setLang('fr'); localStorage.setItem('appLang', 'fr'); setAppState('login'); }} style={{ ...btnStyle, backgroundColor: '#3b82f6' }} onMouseDown={e=>e.target.style.transform='scale(0.95)'} onMouseUp={e=>e.target.style.transform='scale(1)'}>🇫🇷 Français (French)</button>
          <button onClick={() => { setLang('zh'); localStorage.setItem('appLang', 'zh'); setAppState('login'); }} style={{ ...btnStyle, backgroundColor: '#f97316' }} onMouseDown={e=>e.target.style.transform='scale(0.95)'} onMouseUp={e=>e.target.style.transform='scale(1)'}>🇨🇳 中文 (Chinese)</button>
        </div>
      </div>
    );
  }

  if (appState === "login") {
    return (
      <>
        <AuthScreen isDarkMode={isDarkMode} />
        <CookieConsent isDarkMode={isDarkMode} />
      </>
    );
  }

  if (appState === "menu") {
    return (
      <>
        <MenuDashboard 
          openExamInfo={openExamInfo}
          courseLanguage={courseLanguage} setCourseLanguage={setCourseLanguage}
          currentUser={currentUser} setCurrentUser={setCurrentUser} setAppState={setAppState}
          menuPage={menuPage} setMenuPage={setMenuPage} menuTab={menuTab} setMenuTab={setMenuTab}
          statView={statView} setStatView={setStatView} soundEnabled={soundEnabled} setSoundEnabled={setSoundEnabled}
          testMode={testMode} setTestMode={setTestMode} customText={customText} setCustomText={setCustomText}
          testDuration={testDuration} setTestDuration={setTestDuration} openDrillInfo={openDrillInfo} startTypingTest={startTypingTest}
          levels={levels} lessonTitles={lessonTitles} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode}
          lang={lang} setLang={setLang}
          isPremium={isPremium} 
          authScreen={<AuthScreen isDarkMode={isDarkMode} isEmbedded={true} />} 
        />
        <CookieConsent isDarkMode={isDarkMode} />
      </>
    );
  }

  if (appState === "drillInfo") {
    const tStyle = {
      bg: isDarkMode ? '#0f172a' : '#f1f5f9',
      box: isDarkMode ? '#1e293b' : '#f0fdf4', 
      text: isDarkMode ? '#f8fafc' : '#0f172a',
      subtext: isDarkMode ? '#94a3b8' : '#475569',
      label: isDarkMode ? '#38bdf8' : '#065f46',
      border: isDarkMode ? '#059669' : '#bbf7d0',
      line: isDarkMode ? '#334155' : '#dcfce7',
      selectBg: isDarkMode ? '#334155' : 'white',
      selectBorder: isDarkMode ? '#475569' : '#cbd5e1'
    };

    return (
      <div 
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') document.getElementById('btn-cancel')?.focus();
          if (e.key === 'ArrowRight') document.getElementById('btn-ok')?.focus();
        }}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%', backgroundColor: tStyle.bg, padding: '20px', boxSizing: 'border-box', fontFamily: 'sans-serif', transition: 'background-color 0.3s' }}>
        <div style={{ width: '100%', maxWidth: '500px', background: `linear-gradient(${tStyle.box}, ${tStyle.box}) padding-box, linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899) border-box`, border: '4px solid transparent', borderRadius: '24px', padding: '35px', boxShadow: isDarkMode ? '0 15px 40px rgba(0,0,0,0.5)' : '0 15px 40px rgba(0,0,0,0.1)', boxSizing: 'border-box', transition: 'all 0.3s' }}>
          <h2 style={{ margin: '0 0 20px 0', color: isDarkMode ? '#4ade80' : '#166534', fontSize: '24px', borderBottom: `2px solid ${tStyle.line}`, paddingBottom: '15px' }}>Part {currentLevelData?.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px' }}>
            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ width: '120px', fontWeight: 'bold', color: tStyle.label, fontSize: '15px' }}>{text.time || "Time"}:</div>
              <div style={{ flex: 1, color: tStyle.subtext, fontSize: '15px' }}>⏱️ {currentLevelData?.timeLimit ? currentLevelData.timeLimit / 60 : 5} min.</div>
            </div>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <div style={{ width: '120px', fontWeight: 'bold', color: tStyle.label, fontSize: '15px' }}>{text.accuracy || "Accuracy"}:</div>
              <select value={currentGoal.accuracy} onChange={(e) => { const newAcc = Number(e.target.value); const newWpm = newAcc === 90 ? 17 : newAcc === 94 ? 25 : newAcc === 98 ? 30 : 35; setCurrentGoal({ accuracy: newAcc, wpm: newWpm }); }} style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${tStyle.selectBorder}`, backgroundColor: tStyle.selectBg, color: tStyle.text, fontSize: '14px', fontWeight: 'bold', outline: 'none', cursor: 'pointer' }}>
                <option value={90}>90% Relaxed</option>
                <option value={94}>94% Intermediate</option>
                <option value={98}>98% Strict</option>
                <option value={100}>100% Perfect</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ width: '120px', fontWeight: 'bold', color: tStyle.label, fontSize: '15px' }}>{text.targetWpm || "Target WPM"}:</div>
              <div style={{ flex: 1, color: isDarkMode ? '#fca5a5' : '#be123c', fontWeight: 'bold', fontSize: '15px' }}>🎯 {currentGoal.wpm} WPM</div>
            </div>
            <div style={{ display: 'flex', gap: '15px' }}>
              <div style={{ width: '120px', fontWeight: 'bold', color: tStyle.label, fontSize: '15px' }}>Info:</div>
              <div style={{ flex: 1, color: tStyle.subtext, fontSize: '14px', lineHeight: '1.5' }}>{currentLevelData?.instruction}</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
            <button 
              id="btn-cancel"
              onClick={() => setAppState("menu")} 
              onFocus={(e) => e.target.style.outline = `3px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`}
              onBlur={(e) => e.target.style.outline = 'none'}
              style={{ background: 'none', border: 'none', color: tStyle.subtext, fontWeight: 'bold', fontSize: '15px', cursor: 'pointer', textDecoration: 'underline', padding: '5px', borderRadius: '4px' }}>{text.btnCancel || "Cancel"}</button>
            <button 
              id="btn-ok"
              autoFocus onClick={() => { setAppState("practice"); setTimeout(() => inputRef.current?.focus(), 0); }} 
              onFocus={(e) => e.target.style.outline = `3px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`}
              onBlur={(e) => e.target.style.outline = 'none'}
              style={{ padding: '12px 24px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', boxShadow: '0 4px 10px rgba(34, 197, 94, 0.3)' }}>▶ {text.continueBtn || "Continue"} (Space)</button>
          </div>
        </div>
      </div>
    );
  }

  if (appState === "examInfo") {
    const tStyle = {
      bg: isDarkMode ? '#0f172a' : '#f1f5f9',
      box: isDarkMode ? '#1e293b' : '#f0fdf4', 
      text: isDarkMode ? '#f8fafc' : '#0f172a',
      subtext: isDarkMode ? '#94a3b8' : '#475569',
      label: isDarkMode ? '#38bdf8' : '#065f46',
      border: isDarkMode ? '#059669' : '#bbf7d0',
      line: isDarkMode ? '#334155' : '#dcfce7',
      highlightBg: isDarkMode ? '#0f172a' : '#ffffff',
      highlightBorder: isDarkMode ? '#334155' : '#cbd5e1'
    };

    // 1. Calculate Target WPM & Layout Name dynamically
    let targetWpmDisplay = 'N/A';
    let layoutName = 'Unknown Layout';

    if (examPayload?.selectedLang === 'sequential') {
      targetWpmDisplay = `Eng: ${activeExam?.engWpm} ➔ Hin: ${activeExam?.hinWpm} WPM`;
      layoutName = "Bilingual Sequential Mode";
    } else {
      const option = activeExam?.allowedOptions?.find(opt => opt.lang === examPayload?.selectedLang);
      if (option) {
        targetWpmDisplay = `${option.speed} WPM`;
        layoutName = option.label;
      } else {
         // Fallback
         targetWpmDisplay = examPayload?.selectedLang === 'english' ? `${activeExam?.engWpm} WPM` : `${activeExam?.hinWpm} WPM`;
         layoutName = examPayload?.selectedLang === 'english' ? 'English (QWERTY)' : 'Hindi Keyboard';
      }
    }

    // 2. Decode Rules into human-readable descriptions
    const backspaceText = activeExam?.defaultBackspace === 'disabled' 
      ? "Completely Disabled (No corrections allowed)" 
      : activeExam?.defaultBackspace === 'disabled_after_space' 
        ? "Word-Locked (Cannot edit previous words)" 
        : "Fully Enabled (Free editing allowed)";
    
    const scoringText = activeExam?.scoringRule === 'ssc' 
      ? "SSC Formula (Strict half & full mistake calculation)" 
      : "Standard Formula (Net WPM minus errors)";

    return (
      <>
        {/* 🚀 THE NEW ALT CODE MODAL CHEAT SHEET */}
        {showAltGuide && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box', backdropFilter: 'blur(5px)' }}>
            <div style={{ width: '100%', maxWidth: '750px', backgroundColor: isDarkMode ? '#0f172a' : '#ffffff', borderRadius: '20px', border: `2px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: '90vh', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
              
              {/* Modal Header */}
              <div style={{ padding: '20px', backgroundColor: isDarkMode ? '#1e293b' : '#f0f9ff', borderBottom: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ margin: 0, color: isDarkMode ? '#38bdf8' : '#0284c7', fontSize: '22px' }}>💡 Kruti Dev Alt Code Master Guide</h2>
                  <p style={{ margin: '5px 0 0 0', color: tStyle.subtext, fontSize: '13px' }}>Hold the <kbd style={{backgroundColor: '#e2e8f0', color: '#0f172a', padding: '2px 6px', borderRadius: '4px'}}>Alt</kbd> key, type the 4 numbers on your <strong>Numpad</strong>, then release.</p>
                </div>
                <button onClick={() => setShowAltGuide(false)} aria-label="Close Guide" style={{ background: 'none', border: 'none', fontSize: '28px', color: tStyle.subtext, cursor: 'pointer' }}>&times;</button>
              </div>

              {/* Modal Content - The Grid */}
              <div style={{ padding: '20px', overflowY: 'auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
                
               {/* Column 1: Core Characters & Symbols */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: tStyle.subtext, textTransform: 'uppercase', marginBottom: '5px', borderBottom: `1px solid ${tStyle.border}`, paddingBottom: '5px' }}>Core Characters & Symbols</div>
                  {[
                    { char: 'प्र', code: '0231' }, { char: 'क्र', code: '0216' }, 
                    { char: 'फ्र', code: '0221' }, { char: 'स्त्र', code: '0243' }, 
                    { char: 'ट्र', code: '0170' }, { char: 'ड्र', code: '0171' }, 
                    { char: 'ँ (Chandrabindu)', code: '0161' }, { char: 'ऋ', code: '0195' },
                    { char: '“ (Left Quote)', code: '0222' }, { char: '” (Right Quote)', code: '0223' },
                    { char: '¼ (Left Bracket)', code: '0188' }, { char: '½ (Right Bracket)', code: '0189' }
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 15px', backgroundColor: tStyle.highlightBg, borderRadius: '8px', border: `1px solid ${tStyle.highlightBorder}` }}>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', color: tStyle.text }}>{item.char}</span>
                      <span style={{ fontFamily: 'monospace', color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>Alt + {item.code}</span>
                    </div>
                  ))}
                </div>

                {/* Column 2: Complex Conjuncts */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', color: tStyle.subtext, textTransform: 'uppercase', marginBottom: '5px', borderBottom: `1px solid ${tStyle.border}`, paddingBottom: '5px' }}>Complex Conjuncts</div>
                  {[
                    { char: 'ह्य', code: '0225' }, { char: 'हृ', code: '0226' }, 
                    { char: 'ह्म', code: '0227' }, { char: 'क्त', code: '0228' }, 
                    { char: 'द्द', code: '0204' }, { char: 'दृ', code: '0205' },
                    { char: 'द्व', code: '0152' }, { char: 'द्म', code: '0249' },
                    { char: 'द्य', code: '0248' }, { char: 'ट्ट', code: '0234' },
                    { char: 'ठ्ठ', code: '0240' }, { char: 'रु', code: '0035' }
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 15px', backgroundColor: tStyle.highlightBg, borderRadius: '8px', border: `1px solid ${tStyle.highlightBorder}` }}>
                      <span style={{ fontSize: '18px', fontWeight: 'bold', color: tStyle.text }}>{item.char}</span>
                      <span style={{ fontFamily: 'monospace', color: '#10b981', fontWeight: 'bold', fontSize: '16px' }}>Alt + {item.code}</span>
                    </div>
                  ))}
                </div>

              </div>
              
              {/* Modal Footer */}
              <div style={{ padding: '15px 20px', backgroundColor: isDarkMode ? '#1e293b' : '#f8fafc', borderTop: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`, textAlign: 'center' }}>
                <button onClick={() => setShowAltGuide(false)} style={{ padding: '10px 30px', backgroundColor: '#38bdf8', color: '#0f172a', borderRadius: '8px', fontWeight: 'bold', border: 'none', cursor: 'pointer', boxShadow: '0 4px 10px rgba(56, 189, 248, 0.3)' }}>Got it, I'm ready!</button>
              </div>
            </div>
          </div>
        )}
      <div 
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'Escape') document.getElementById('btn-cancel')?.click();
          if (e.key === 'ArrowRight' || e.key === 'Enter') document.getElementById('btn-ok')?.click();
        }}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%', backgroundColor: tStyle.bg, padding: '20px', boxSizing: 'border-box', fontFamily: 'sans-serif', transition: 'background-color 0.3s' }}>
        
        <div style={{ 
          width: '100%', maxWidth: '950px', 
          background: `linear-gradient(${tStyle.box}, ${tStyle.box}) padding-box, linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899) border-box`, 
          border: '4px solid transparent', borderRadius: '24px', padding: isMobile ? '25px' : '40px', 
          boxShadow: isDarkMode ? '0 15px 40px rgba(0,0,0,0.5)' : '0 15px 40px rgba(0,0,0,0.1)', 
          boxSizing: 'border-box', transition: 'all 0.3s', display: 'flex', flexDirection: 'column' 
        }}>
          
          <h2 style={{ margin: '0 0 25px 0', color: isDarkMode ? '#4ade80' : '#166534', fontSize: isMobile ? '22px' : '28px', borderBottom: `2px solid ${tStyle.line}`, paddingBottom: '15px' }}>
            {activeExam?.name || "Exam Configuration"}
          </h2>

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '25px' : '40px', marginBottom: '30px' }}>
            
            {/* LEFT COLUMN: Deep Description */}
            <div style={{ flex: '1 1 50%' }}>
              <h3 style={{ color: tStyle.label, fontSize: '18px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 15px 0' }}>
                 📝 Exam Overview
              </h3>
              <p style={{ color: tStyle.text, fontSize: '15px', lineHeight: '1.7', marginBottom: '20px', minHeight: '60px' }}>
                {activeExam?.description}
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', backgroundColor: tStyle.highlightBg, padding: '20px', borderRadius: '16px', border: `1px solid ${tStyle.highlightBorder}` }}>
                <div>
                  <strong style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Evaluation Matrix:</strong>
                  <div style={{ color: tStyle.text, fontSize: '15px', marginTop: '4px', fontWeight: 'bold' }}>{scoringText}</div>
                </div>
                <div>
                  <strong style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Backspace Rule:</strong>
                  <div style={{ color: tStyle.text, fontSize: '15px', marginTop: '4px', fontWeight: 'bold' }}>{backspaceText}</div>
                </div>
                {activeExam?.type === 'sequential' && (
                   <div style={{ marginTop: '5px', paddingTop: '15px', borderTop: `1px dashed ${tStyle.highlightBorder}` }}>
                    <strong style={{ color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Simulation Flow:</strong>
                    <div style={{ color: '#f59e0b', fontSize: '14px', marginTop: '4px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>English</span> ➔ <span>2 Min Break</span> ➔ <span>Hindi</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Requirements & Passage Selection */}
            <div style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <h3 style={{ color: tStyle.label, fontSize: '18px', margin: '0 0 5px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                 ⚙️ Session Requirements
              </h3>

              {/* Requirement Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ backgroundColor: tStyle.highlightBg, padding: '15px', borderRadius: '12px', border: `1px solid ${tStyle.highlightBorder}` }}>
                  <div style={{ fontSize: '11px', color: tStyle.subtext, textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '5px' }}>Duration</div>
                  <div style={{ fontSize: '18px', fontWeight: 'bold', color: tStyle.text }}>⏱️ {examPayload?.timeInMinutes || 10} Min</div>
                </div>
                <div style={{ backgroundColor: tStyle.highlightBg, padding: '15px', borderRadius: '12px', border: `1px solid ${tStyle.highlightBorder}` }}>
                  <div style={{ fontSize: '11px', color: tStyle.subtext, textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '5px' }}>Target Speed</div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold', color: isDarkMode ? '#fca5a5' : '#be123c' }}>🎯 {targetWpmDisplay}</div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: tStyle.highlightBg, padding: '15px', borderRadius: '12px', border: `1px solid ${tStyle.highlightBorder}` }}>
                <div style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase' }}>Min. Accuracy</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981' }}>{activeExam?.requiredAccuracy || 95}%</div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', backgroundColor: tStyle.highlightBg, padding: '15px', borderRadius: '12px', border: `1px solid ${tStyle.highlightBorder}` }}>
                <div style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase' }}>Selected Medium</div>
                <div style={{ fontSize: '15px', fontWeight: 'bold', color: isDarkMode ? '#38bdf8' : '#0284c7' }}>⌨️ {layoutName}</div>
                
                {/* 🚀 NEW: KRUTI DEV CHEAT SHEET BUTTON */}
                {(layoutName.includes('Kruti') || examPayload?.selectedLang === 'kruti_dev') && (
                  <button 
                    onClick={() => setShowAltGuide(true)}
                    style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f59e0b', color: 'white', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'transform 0.1s' }}
                    onMouseDown={e => e.target.style.transform = 'scale(0.97)'} 
                    onMouseUp={e => e.target.style.transform = 'scale(1)'}
                  >
                    💡 View Kruti Dev Alt Code Guide
                  </button>
                )}
              </div>



              {/* 🚀 TWO-STEP PASSAGE SELECTION (UPGRADED FOR CATEGORIES) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginTop: '10px' }}>
                
                {/* Step 1: Choose Source Category */}
                <div>
                  <div style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>📄 Passage Source</div>
                  <div style={{ position: 'relative' }}>
                    <select 
                      value={passageSource}
                      onChange={(e) => { 
                        setPassageSource(e.target.value); 
                        setSelectedPassageId("random"); // Reset the second dropdown automatically
                      }}
                      style={{ 
                        width: '100%', padding: '14px 40px 14px 15px', borderRadius: '12px', 
                        border: `2px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`,
                        backgroundColor: tStyle.highlightBg, color: tStyle.text, 
                        fontSize: '14px', fontWeight: '600', outline: 'none', cursor: 'pointer',
                        appearance: 'none', transition: 'border-color 0.2s'
                      }}
                      onFocus={e => e.target.style.borderColor = isDarkMode ? '#38bdf8' : '#0284c7'}
                      onBlur={e => e.target.style.borderColor = isDarkMode ? '#334155' : '#cbd5e1'}
                    >
                      <option value="random">🎲 Random Mix (All Categories)</option>
                      <option value="pyq">🏛️ Previous Year Questions (PYQs)</option>
                      <option value="current_affairs">📰 Daily Current Affairs (The Hindu)</option>
                      <option value="subject">📚 Subject Practice (Geo, Pol, History)</option>
                      <option value="custom">✍️ Paste Custom Text</option>
                    </select>
                    <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '14px', color: tStyle.subtext }}>▼</span>
                  </div>
                </div>

                {/* Step 2A: Specific Category Dropdown */}
                {(passageSource === 'pyq' || passageSource === 'current_affairs' || passageSource === 'subject') && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <div style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>
                       {passageSource === 'current_affairs' ? "📰 Select Today's Editorial" : "📚 Choose Specific Passage"}
                    </div>
                    <div style={{ position: 'relative' }}>
                      <select 
                        value={selectedPassageId}
                        onChange={(e) => setSelectedPassageId(e.target.value)}
                        style={{ 
                          width: '100%', padding: '14px 40px 14px 15px', borderRadius: '12px', 
                          border: `2px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`,
                          backgroundColor: isDarkMode ? '#1e293b' : '#f0f9ff', color: tStyle.text, 
                          fontSize: '14px', fontWeight: '600', outline: 'none', cursor: 'pointer',
                          appearance: 'none'
                        }}
                      >
                        <option value="random" disabled>Select a passage...</option>
                        
                        {/* Filters the library to ONLY show items matching the selected category! */}
                        {PASSAGE_LIBRARY.filter(p => p.isPublished && p.sourceType === passageSource).map((passage) => (
                          <option key={passage.id} value={passage.id}>
                            {passage.category} - {passage.title}
                          </option>
                        ))}
                        
                      </select>
                      <span style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', fontSize: '14px', color: tStyle.subtext }}>▼</span>
                    </div>

                    {/* Metadata Readout */}
                    {selectedPassageId !== 'random' && (
                      <div style={{ marginTop: '12px', padding: '12px', backgroundColor: tStyle.highlightBg, border: `1px solid ${tStyle.highlightBorder}`, borderRadius: '8px', fontSize: '13px', color: tStyle.text, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div><strong>Words:</strong> <span style={{color: '#10b981', marginLeft: '4px'}}>{PASSAGE_LIBRARY.find(p => p.id === selectedPassageId)?.words || 0}</span></div>
                        <div style={{width: '1px', height: '15px', backgroundColor: tStyle.highlightBorder}}></div>
                        <div><strong>Keystrokes:</strong> <span style={{color: '#38bdf8', marginLeft: '4px'}}>{PASSAGE_LIBRARY.find(p => p.id === selectedPassageId)?.keystrokes || 0}</span></div>
                        <div style={{width: '1px', height: '15px', backgroundColor: tStyle.highlightBorder}}></div>
                        <div><strong>Duration:</strong> <span style={{color: '#f59e0b', marginLeft: '4px'}}>{PASSAGE_LIBRARY.find(p => p.id === selectedPassageId)?.duration || "10 Min"}</span></div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Step 2B: Custom Text Area */}
                {passageSource === 'custom' && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                    <div style={{ fontSize: '12px', color: tStyle.subtext, fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '5px' }}>✍️ Paste Your Text</div>
                    <textarea 
                      value={customText}
                      onChange={(e) => setCustomText(e.target.value)}
                      placeholder="Paste your specific exam paragraph here..."
                      style={{
                        width: '100%', height: '80px', padding: '12px', borderRadius: '12px',
                        border: `2px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`,
                        backgroundColor: isDarkMode ? '#1e293b' : '#f0f9ff', color: tStyle.text,
                        fontSize: '14px', outline: 'none', resize: 'none', boxSizing: 'border-box'
                      }}
                    />
                  </motion.div>
                )}
              </div>
            </div>
          </div>

          {/* BOTTOM BAR: Buttons */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '20px', borderTop: `1px solid ${tStyle.line}` }}>
            <button 
              id="btn-cancel"
              onClick={() => setAppState("menu")} 
              onFocus={(e) => e.target.style.outline = `3px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`}
              onBlur={(e) => e.target.style.outline = 'none'}
              style={{ background: 'none', border: 'none', color: tStyle.subtext, fontWeight: 'bold', fontSize: '16px', cursor: 'pointer', textDecoration: 'underline', padding: '5px', borderRadius: '4px' }}
            >
              Go Back
            </button>
            <button 
              id="btn-ok"
              autoFocus 
              onClick={() => { 
                // 🚀 1. GUEST USER CHECK (Not logged in)
                if (!currentUser) {
                  alert("🔒 Please log in or create a free account first to access Exam Simulations.");
                  setAppState("menu"); // Close exam info
                  setMenuTab("login"); // Send to Login page
                  return;
                }

                // 🚀 2. FREE USER CHECK (Logged in, but no active plan)
                if (!isPremium) {
                  alert("🌟 Indian Exam Simulations are a Premium feature. Please upgrade to unlock full access.");
                  setAppState("menu"); // Close exam info
                  setMenuTab("upgrade"); // Send to Pricing page
                  return;
                }

                let textPayload;

                // Handle Custom Text
                if (passageSource === "custom") {
                  if (!customText || !customText.trim()) return alert("Please paste some text first!");
                  
                  if (examPayload.selectedLang === 'sequential') {
                    textPayload = { eng: customText, hin: customText };
                  } else {
                    textPayload = customText;
                  }
                } 
                // Handle Library or Random Text
                else {
                  let activeLibrary = PASSAGE_LIBRARY.filter(p => p.isPublished);

                  if (passageSource === 'pyq' || passageSource === 'current_affairs' || passageSource === 'subject') {
                     activeLibrary = activeLibrary.filter(p => p.sourceType === passageSource);
                  }

                  let chosenPassage;

                  if (passageSource === "random" || selectedPassageId === "random" || !selectedPassageId) {
                    chosenPassage = activeLibrary[Math.floor(Math.random() * activeLibrary.length)] || PASSAGE_LIBRARY[0];
                  } else {
                    chosenPassage = activeLibrary.find(p => p.id.toString() === selectedPassageId.toString()) || activeLibrary[0];
                  }

                  const isKruti = layoutName.toLowerCase().includes('kruti') || 
                                  examPayload.selectedLang === 'kruti_dev' || 
                                (examPayload.selectedLang === 'sequential' && activeExam?.allowedOptions?.[0]?.label.includes('Kruti'));
                  
                  const hindiPassage = isKruti ? (chosenPassage.textHinKruti || chosenPassage.textHin) : (chosenPassage.textHinUnicode || chosenPassage.textHin);

                  if (examPayload.selectedLang === 'sequential') {
                    textPayload = { eng: chosenPassage.textEng, hin: hindiPassage };
                  } else if (examPayload.selectedLang === 'english') {
                    textPayload = chosenPassage.textEng;
                  } else {
                    textPayload = hindiPassage;
                  }
                }

                // Set Sequential Machine States
                if (examPayload.selectedLang === 'sequential') {
                  setSeqPhase(1);
                  setSeqRestTime(120);
                  setSeqResults({ eng: null, hin: null });
                } else {
                  setSeqPhase(0);
                }

                // Update payload and Launch!
                setExamPayload(prev => ({ ...prev, generatedText: textPayload }));
                startTypingTest("custom", textPayload, examPayload.timeInMinutes * 60, examPayload.selectedLang); 
              }}
              
              onFocus={(e) => e.target.style.outline = `3px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`}
              onBlur={(e) => e.target.style.outline = 'none'}
              style={{ 
                padding: '14px 28px', 
                backgroundColor: isPremium ? '#22c55e' : '#f97316', 
                color: 'white', border: 'none', borderRadius: '12px', 
                cursor: 'pointer', fontWeight: 'bold', fontSize: '16px', 
                boxShadow: isPremium ? '0 4px 15px rgba(34, 197, 94, 0.3)' : '0 4px 15px rgba(249, 115, 22, 0.3)', 
                transition: 'transform 0.1s' 
              }}
              onMouseDown={e => e.target.style.transform = 'scale(0.96)'} 
              onMouseUp={e => e.target.style.transform = 'scale(1)'}
            >
              {/* 🚀 DYNAMIC TEXT: Changes based on Guest vs Free vs Premium */}
              {isPremium ? '▶ Start Exam Simulation' : (!currentUser ? '🔒 Login to Start Exam' : '🔒 Upgrade to Start Exam')}
            </button>
          </div>
        </div>
      </div>
      </>
    );
  }

  if (appState === "practice") {
    
    // ========================================================
    // 🚀 EXAM SIMULATION ENGINE (Strictly for Indian Exams)
    // ========================================================
    if (activeExam) {
      
      // --- PHASE 2: REST SCREEN ---
      if (seqPhase === 2) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', color: isDarkMode ? '#f8fafc' : '#0f172a' }}>
            <h2 style={{ color: '#10b981', fontSize: '30px', marginBottom: '10px' }}>✅ English Test Complete</h2>
            <div style={{ backgroundColor: isDarkMode ? '#1e293b' : 'white', padding: '40px', borderRadius: '20px', border: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '60px', marginBottom: '10px' }}>☕</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px' }}>Mandatory Rest Period</h3>
              <div style={{ fontSize: '48px', fontWeight: 'bold', fontFamily: 'monospace', color: isDarkMode ? '#38bdf8' : '#0284c7' }}>
                {Math.floor(seqRestTime / 60)}:{String(seqRestTime % 60).padStart(2, '0')}
              </div>
              <p style={{ color: '#64748b', marginTop: '20px', maxWidth: '300px' }}>Shake out your hands. The Hindi typing test will begin automatically when the timer reaches zero.</p>
            </div>
            <button onClick={() => setSeqPhase(3)} style={{ marginTop: '30px', padding: '12px 24px', backgroundColor: 'transparent', color: '#64748b', border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              Skip Break & Start Hindi
            </button>
          </div>
        );
      }

      // --- PHASE 4: COMBINED RESULTS SCREEN ---
      if (seqPhase === 4) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: isDarkMode ? '#0f172a' : '#f8fafc', padding: '20px' }}>
             <div style={{ width: '100%', maxWidth: '800px', backgroundColor: isDarkMode ? '#1e293b' : 'white', borderRadius: '20px', border: `2px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`, padding: '40px', boxShadow: '0 20px 50px rgba(0,0,0,0.1)' }}>
                <h2 style={{ textAlign: 'center', color: isDarkMode ? '#fde047' : '#ca8a04', fontSize: '32px', margin: '0 0 30px 0' }}>🏆 Full Mock Test Results</h2>
                
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  {/* English Card */}
                  <div style={{ flex: 1, minWidth: '250px', padding: '20px', backgroundColor: isDarkMode ? '#0f172a' : '#f1f5f9', borderRadius: '12px', border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}` }}>
                    <h3 style={{ margin: '0 0 15px 0', color: isDarkMode ? '#38bdf8' : '#0284c7' }}>🇺🇸 English Module</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: 'bold' }}><span>Net Speed:</span> <span>{seqResults.eng?.netWpm || 0} WPM</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: 'bold' }}><span>Accuracy:</span> <span>{seqResults.eng?.accuracy || 0}%</span></div>
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `1px dashed ${isDarkMode ? '#334155' : '#cbd5e1'}`, fontWeight: 'bold', textAlign: 'center', color: (seqResults.eng?.netWpm >= activeExam.engWpm) ? '#10b981' : '#ef4444' }}>
                      {(seqResults.eng?.netWpm >= activeExam.engWpm) ? '✅ Module Passed' : '❌ Module Failed'}
                    </div>
                  </div>

                  {/* Hindi Card */}
                  <div style={{ flex: 1, minWidth: '250px', padding: '20px', backgroundColor: isDarkMode ? '#0f172a' : '#f1f5f9', borderRadius: '12px', border: `1px solid ${isDarkMode ? '#334155' : '#cbd5e1'}` }}>
                    <h3 style={{ margin: '0 0 15px 0', color: isDarkMode ? '#10b981' : '#059669' }}>🇮🇳 Hindi Module</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: 'bold' }}><span>Net Speed:</span> <span>{seqResults.hin?.netWpm || 0} WPM</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: isDarkMode ? '#f8fafc' : '#0f172a', fontWeight: 'bold' }}><span>Accuracy:</span> <span>{seqResults.hin?.accuracy || 0}%</span></div>
                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `1px dashed ${isDarkMode ? '#334155' : '#cbd5e1'}`, fontWeight: 'bold', textAlign: 'center', color: (seqResults.hin?.netWpm >= activeExam.hinWpm) ? '#10b981' : '#ef4444' }}>
                      {(seqResults.hin?.netWpm >= activeExam.hinWpm) ? '✅ Module Passed' : '❌ Module Failed'}
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: 'center', marginTop: '40px' }}>
                   <button onClick={() => { setAppState("menu"); setActiveExam(null); setSeqPhase(0); }} style={{ padding: '15px 30px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '12px', fontSize: '18px', fontWeight: 'bold', cursor: 'pointer' }}>
                     Return to Dashboard
                   </button>
                </div>
             </div>
          </div>
        );
      }

      // --- PHASE 1 & 3: THE ACTUAL TESTS ---
      const isSeqMode = examPayload.selectedLang === 'sequential';
      
      const activeText = isSeqMode 
        ? (seqPhase === 1 ? examPayload.generatedText.eng : examPayload.generatedText.hin) 
        : examPayload.generatedText;
      
      const isKrutiMode = examPayload?.selectedLang?.includes('kruti') || 
                          activeExam?.allowedOptions?.some(opt => opt.lang === examPayload?.selectedLang && opt.label.toLowerCase().includes('kruti'));

      const activeLanguage = isSeqMode 
        ? (seqPhase === 1 ? 'english' : 'kruti_dev') 
        : (isKrutiMode ? 'kruti_dev' : examPayload?.selectedLang);

      const activeTargetWpm = isSeqMode
        ? (seqPhase === 1 ? activeExam.engWpm : activeExam.hinWpm)
        : (examPayload.selectedLang === 'hindi' ? activeExam.hinWpm : activeExam.engWpm);

      return (
        <div style={{ backgroundColor: isDarkMode ? '#0f172a' : '#f1f5f9', minHeight: '100vh', width: '100%', boxSizing: 'border-box' }}>
          <Suspense fallback={<div style={{ textAlign: 'center', padding: '100px', color: '#94a3b8' }}>Loading simulation engine...</div>}>
            <ActiveTypingTest 
              settings={{ 
                mode: 'time', 
                level: examPayload.timeInMinutes * 60,
                backspaceMode: examPayload.defaultBackspace 
              }}
              customText={activeText} 
              isDarkMode={isDarkMode}
              isIndianExam={true}
              typingLanguage={activeLanguage}
              examScoringRule={examPayload.scoringRule} 
              targetWpm={activeTargetWpm} 
              targetAccuracy={activeExam.requiredAccuracy ? parseInt(activeExam.requiredAccuracy) : 95} 
              zenMode={localStorage.getItem('zenMode') === 'true'}
              cursorStyle={localStorage.getItem('cursorStyle') || 'line'}
              soundProfile={localStorage.getItem('soundProfile') || 'modern'}
              soundEnabled={soundEnabled}
              isSequentialPhase={isSeqMode} 

              onCancel={(stats) => { 
                if (isSeqMode) {
                  if (stats && seqPhase === 1) {
                    saveExamToDatabase(stats); 
                    setSeqResults(p => ({ ...p, eng: stats }));
                    setSeqPhase(2);
                  } else if (stats && seqPhase === 3) {
                    saveExamToDatabase(stats); 
                    setSeqResults(p => ({ ...p, hin: stats }));
                    setSeqPhase(4);
                  } else {
                    setIsTimerRunning(false);
                    setAppState("menu"); 
                    setActiveExam(null); 
                    setSeqPhase(0);
                  }
                } else if (activeExam) {
                  if (stats) saveExamToDatabase(stats); 
                  setIsTimerRunning(false); 
                  setAppState("menu"); 
                  setActiveExam(null); 
                } else {
                  // 🚀 DEMO & PARAGRAPH EXIT LOGIC 
                  if (stats) {
                    setWpm(stats.netWpm);
                    setGrossWpm(stats.grossWpm);
                    setAccuracy(stats.accuracy);
                    
                    // 🔒 THE FIX: FIRE THE GUEST TRAP HERE FOR THE ADVANCED ENGINE!
                    if (!currentUser) {
                      if (isTypingTest) {
                        localStorage.setItem('guest_demo_taken', 'true');
                      } else {
                        const currentCount = parseInt(localStorage.getItem('guest_drill_count') || '0');
                        localStorage.setItem('guest_drill_count', (currentCount + 1).toString());
                      }
                    }

                    if (!isTypingTest) {
                      saveProgressToDatabase(currentLevel, stats.netWpm, stats.grossWpm, stats.accuracy);
                    }
                  }
                  setIsTimerRunning(false);
                  setAppState("results");
                }
              }}
            />
          </Suspense>
        </div>
      );
    }

    // --- NORMAL LEARNING PRACTICE RENDERING BELOW ---
    const tStyle = {
      bg: isDarkMode ? '#0f172a' : '#f1f5f9',
      box: isDarkMode ? '#1e293b' : '#ffffff', 
      sidebar: isDarkMode ? '#0f172a' : '#f8fafc',
      text: isDarkMode ? '#f8fafc' : '#0f172a',
      subtext: isDarkMode ? '#94a3b8' : '#64748b',
      border: isDarkMode ? '#334155' : '#e2e8f0',
      popBg: isDarkMode ? '#1e293b' : 'white',
      popText: isDarkMode ? '#f8fafc' : '#475569',
      overlay: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(15, 23, 42, 0.8)'
    };

    if (isReadingLesson) {
       const rawText = currentLevelData?.text || "";
       const textArray = Array.isArray(rawText) ? rawText : rawText.split('\n');

       return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%', backgroundColor: tStyle.bg, padding: isMobile ? '10px' : '20px', boxSizing: 'border-box', fontFamily: 'sans-serif', transition: 'background-color 0.3s' }}>
             
             {enlargedImage && (
                <div 
                  onClick={() => setEnlargedImage(null)} 
                  style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.9)', zIndex: 99999, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'zoom-out', backdropFilter: 'blur(5px)' }}
                >
                  <img 
                    src={enlargedImage} 
                    alt="Enlarged View" 
                    width="2828"
                    height="2828"
                    style={{ 
                      maxWidth: '90%', 
                      maxHeight: '90%', 
                      objectFit: 'contain', 
                      borderRadius: '16px', 
                      boxShadow: '0 20px 50px rgba(0,0,0,0.5)', 
                      border: '2px solid #38bdf8' 
                    }} 
                  />
                  <div style={{ position: 'absolute', top: '30px', right: '40px', color: 'white', fontSize: '40px', fontWeight: 'bold', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>&times;</div>
                  <div style={{ position: 'absolute', bottom: '30px', color: '#94a3b8', fontSize: '16px', fontWeight: 'bold', backgroundColor: 'rgba(0,0,0,0.5)', padding: '8px 16px', borderRadius: '20px' }}>Click anywhere to close</div>
                </div>
             )}

             <div style={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '950px', height: isMobile ? 'auto' : '550px', minHeight: '80vh', background: `linear-gradient(${tStyle.box}, ${tStyle.box}) padding-box, linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899) border-box`, border: '4px solid transparent', borderRadius: isMobile ? '16px' : '24px', boxShadow: isDarkMode ? '0 15px 40px rgba(0,0,0,0.5)' : '0 15px 40px rgba(0,0,0,0.1)', overflow: 'hidden', padding: isMobile ? '20px' : '30px 40px', boxSizing: 'border-box', transition: 'background-color 0.3s' }}>
                <h2 style={{ color: isDarkMode ? '#38bdf8' : '#0284c7', fontSize: isMobile ? '20px' : '24px', marginTop: 0, borderBottom: `2px solid ${tStyle.border}`, paddingBottom: '10px', marginBottom: '10px' }}>📖 Part {levelTitle}</h2>
                <h3 style={{ color: tStyle.subtext, fontSize: '15px', marginTop: 0, marginBottom: '20px' }}>{currentLevelData?.instruction}</h3>
                <div style={{ flex: 1, overflowY: 'hidden', display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: '20px', alignItems: 'stretch', paddingRight: '5px' }}>
                    <div style={{ flex: 1.2, width: '100%', fontSize: '15px', lineHeight: '1.6', color: tStyle.text, backgroundColor: tStyle.sidebar, padding: '20px', borderRadius: '12px', border: `1px solid ${tStyle.border}`, boxSizing: 'border-box', height: isMobile ? 'auto' : '100%', overflowY: 'auto' }}>
                       {textArray.map((paragraph, i) => (
                          <p key={i} style={{ margin: i === textArray.length - 1 ? 0 : '0 0 15px 0' }}>{paragraph}</p>
                       ))}
                    </div>
                    {currentLevelData?.image && (
                       <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: isMobile ? 'auto' : '100%' }}>
                           <img 
                              src={currentLevelData.image} 
                              alt="Lesson Visual" 
                              fetchpriority="high"
                              loading="eager"
                              width="2828"
                              height="2828"
                              onClick={(e) => setEnlargedImage(e.target.src)} 
                              style={{ 
                                cursor: 'zoom-in', 
                                width: '100%', 
                                maxWidth: '100%', 
                                maxHeight: isMobile ? '220px' : '100%', 
                                objectFit: 'contain', 
                                borderRadius: '8px', 
                                border: `2px solid ${tStyle.border}`, 
                                boxShadow: '0 4px 10px rgba(0,0,0,0.05)' 
                              }} 
                            />
                       </div>
                    )}
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', gap: '10px', justifyContent: 'space-between', marginTop: '20px', paddingTop: '15px', borderTop: `2px solid ${tStyle.border}` }}>
                   <button onClick={() => setAppState("menu")} style={{ padding: '10px 20px', backgroundColor: tStyle.sidebar, color: tStyle.text, border: `1px solid ${tStyle.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>{text.btnMenu || "Menu"}</button>
                   <button onClick={() => { saveProgressToDatabase(currentLevel, 0, 0, 100); openDrillInfo(currentLevel + 1); }} style={{ padding: '10px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)' }}>{text.partDone || "Done"} ➡️</button>
                </div>
             </div>
          </div>
       );
    }

    let textBlocks = [];
    if (useBlockUI && textToType) {
      let currentWord = [];
      textToType.split('').forEach((char, index) => {
        currentWord.push({ char, index });
        if (char === ' ') { textBlocks.push(currentWord); currentWord = []; }
      });
      if (currentWord.length > 0) textBlocks.push(currentWord);
    }

    const targetWpm = isTypingTest ? 40 : currentGoal.wpm;
    const targetAcc = isTypingTest ? 95 : currentGoal.accuracy;

    return (
      <div onClick={() => !showEarlyExit && inputRef.current?.focus()} style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%', boxSizing: 'border-box', padding: isMobile ? '10px' : '20px', fontFamily: 'sans-serif', backgroundColor: tStyle.bg, color: tStyle.text, cursor: 'pointer', transition: 'background-color 0.3s' }}>
        
        {showEarlyExit && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: tStyle.overlay, zIndex: 999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: isMobile ? '20px' : '0', boxSizing: 'border-box' }}>
            <div style={{ backgroundColor: tStyle.popBg, padding: isMobile ? '25px' : '40px', borderRadius: '16px', textAlign: 'center', width: '100%', maxWidth: '450px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
                <h2 style={{ fontSize: isMobile ? '20px' : '24px', color: '#16a34a', marginBottom: '10px' }}>🌟 Excellent Performance!</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div onClick={() => { setIsTimerRunning(false); setShowEarlyExit(false); setAppState("results"); }} style={{ width: '100%', boxSizing: 'border-box', padding: '12px', backgroundColor: '#22c55e', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>✅ {text.btnEndEarly || "End Early"} (ENTER)</div>
                  <div onClick={() => { setShowEarlyExit(false); setIsTimerRunning(true); setTimeout(() => inputRef.current?.focus(), 0); }} style={{ width: '100%', boxSizing: 'border-box', padding: '12px', backgroundColor: tStyle.sidebar, color: tStyle.text, border: `2px solid ${tStyle.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>▶️ {text.continueBtn || "Continue"} (SPACE)</div>
                </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', width: '100%', maxWidth: '950px', minHeight: isMobile ? 'auto' : '550px', background: `linear-gradient(${tStyle.box}, ${tStyle.box}) padding-box, linear-gradient(135deg, #06b6d4, #8b5cf6, #ec4899) border-box`, border: '4px solid transparent', borderRadius: isMobile ? '16px' : '24px', boxShadow: isDarkMode ? '0 15px 40px rgba(0,0,0,0.5)' : '0 15px 40px rgba(0,0,0,0.1)', overflow: isMobile ? 'visible' : 'hidden', transition: 'background-color 0.3s' }}>
          
          <div style={{ flex: 1, padding: isMobile ? '15px' : '30px 40px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', minWidth: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '10px', borderBottom: `2px solid ${tStyle.border}`, paddingBottom: '10px' }}>
              <button onClick={() => { setIsTimerRunning(false); setAppState("menu"); }} style={{ padding: '6px 10px', backgroundColor: tStyle.sidebar, color: tStyle.text, border: `1px solid ${tStyle.border}`, borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>{text.btnMenu || "Menu"}</button>
              
              <h3 style={{ color: tStyle.text, margin: 0, fontSize: isMobile ? '13px' : '16px', display: 'flex', alignItems: 'center' }}>
                <span style={{ backgroundColor: isDarkMode ? '#0c4a6e' : '#e0f2fe', padding: '4px 8px', borderRadius: '4px', fontWeight: 'bold', color: isDarkMode ? '#38bdf8' : '#0284c7' }}>
                  {appState === 'practice' && activeExam ? activeExam.name : (isTypingTest ? text.freeTest : `Part ${levelTitle}`)}
                </span>
              </h3>
              
              <div style={{ fontSize: '12px', fontWeight: 'bold', color: tStyle.subtext }}>
                {activeExam ? (courseLanguage === 'hindi' ? `${activeExam.hinWpm} WPM` : `${activeExam.engWpm} WPM`) : (isTypingTest ? text.doYourBest : `${text.targetWpm} ${currentGoal.wpm}`)}
              </div>
            </div>
            
            {useSplitScreen ? (
              <PracticeArea 
                typingLanguage={typingLanguage} 
                useSplitScreen={useSplitScreen} 
                useBlockUI={useBlockUI} 
                textToType={textToType} 
                userInput={userInput} 
                currentError={currentError} 
                textBlocks={textBlocks} 
                errorIndexes={errorIndexes} 
                isDarkMode={isDarkMode} 
                setTargetKeyToHighlight={setTargetKeyToHighlight} // 🚀 ADD THIS
                setNeedsShift={setNeedsShift}                     // 🚀 ADD THIS
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', flexGrow: 1 }}>
                <PracticeArea 
                  typingLanguage={typingLanguage} 
                  useSplitScreen={useSplitScreen} 
                  useBlockUI={useBlockUI} 
                  textToType={textToType} 
                  userInput={userInput} 
                  currentError={currentError} 
                  textBlocks={textBlocks} 
                  errorIndexes={errorIndexes} 
                  isDarkMode={isDarkMode} 
                  setTargetKeyToHighlight={setTargetKeyToHighlight}
                  setNeedsShift={setNeedsShift}
                />
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', transform: isMobile ? 'scale(0.52)' : 'scale(1)', transformOrigin: 'top center', marginTop: isMobile ? '10px' : 'auto', marginBottom: isMobile ? '-90px' : '0' }}>
                  <VirtualKeyboard 
                    targetKeyToHighlight={targetKeyToHighlight}
                    needsShift={needsShift}
                    activeKey={activeKey} 
                    isDarkMode={isDarkMode} 
                    typingLanguage={typingLanguage} 
                  />
                </div>
              </div>
            )}
          </div>

          <div style={{ width: isMobile ? '100%' : '260px', flexShrink: 0, backgroundColor: tStyle.sidebar, padding: isMobile ? '20px 15px' : '30px 20px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box', borderLeft: isMobile ? 'none' : `2px solid ${tStyle.border}`, borderTop: isMobile ? `2px solid ${tStyle.border}` : 'none', borderBottomLeftRadius: isMobile ? '12px' : '0', borderBottomRightRadius: isMobile ? '12px' : '0', transition: 'background-color 0.3s' }}>
            
            <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '10px' }}>
              <h3 style={{ color: tStyle.text, borderBottom: isMobile ? 'none' : `2px solid ${tStyle.border}`, paddingBottom: isMobile ? '0' : '10px', margin: 0, fontSize: '16px', textAlign: 'center', width: '100%' }}>{text.liveProgress || "Live Progress"}</h3>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ textAlign: 'center', backgroundColor: isPaused ? (isDarkMode ? '#450a0a' : '#fef2f2') : 'transparent', padding: '8px 15px', borderRadius: '8px', border: isPaused ? '2px solid #ef4444' : `1px solid ${tStyle.border}`, boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                  <div style={{ fontSize: '10px', fontWeight: 'bold', color: isPaused ? '#ef4444' : tStyle.subtext, textTransform: 'uppercase' }}>
                    {(isTypingTest && testDuration === 3600) ? "Words" : (text.time || "Time")}
                  </div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: isPaused ? '#ef4444' : tStyle.text, fontFamily: 'monospace' }}>
                    {(isTypingTest && testDuration === 3600) 
                      ? `${userInput.trim() === "" ? 0 : userInput.trim().split(/\s+/).length} / ${textToType.trim().split(/\s+/).filter(w => w.length > 0).length}` 
                      : `${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}`
                    }
                  </div>
                </div>

                <button 
                  onClick={(e) => {
                    e.stopPropagation(); 
                    if (isTimerRunning) {
                        setIsTimerRunning(false);
                        setIsPaused(true);
                        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
                    } else {
                        setIsTimerRunning(true);
                        setIsPaused(false);
                        resetInactivityTimer();
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }
                  }}
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '42px', height: '42px', borderRadius: '8px', backgroundColor: isPaused ? '#16a34a' : tStyle.box, color: isPaused ? 'white' : tStyle.text, border: `2px solid ${isPaused ? '#15803d' : tStyle.border}`, cursor: 'pointer', fontSize: '18px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', transition: 'all 0.2s' }}>
                  {isPaused ? '▶️' : '⏸️'}
                </button>
              </div>
            </div>

            {/* 🚀 1. NEW STANDALONE ACCURACY BOX */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 15px', backgroundColor: isDarkMode ? '#1e293b' : 'white', borderRadius: '10px', border: `1px solid ${tStyle.border}`, marginBottom: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
              <span style={{ fontSize: '12px', fontWeight: 'bold', color: tStyle.subtext, textTransform: 'uppercase' }}>Accuracy</span>
              <span style={{ fontWeight: 'bold', fontSize: '15px', color: accuracy >= targetAcc ? '#10b981' : (accuracy >= targetAcc - 4 ? '#f59e0b' : '#ef4444') }}>
                 {accuracy}% {accuracy >= targetAcc ? '✅' : (accuracy >= targetAcc - 4 ? '⚠️' : '❌')}
              </span>
            </div>

            {/* 🚀 2. NEW 10-CANDLE SMOOTH SPEEDOMETER (Expands to fill vertical space) */}
            <div style={{ flexGrow: 1, backgroundColor: 'transparent', border: `2px solid ${tStyle.border}`, borderRadius: '12px', display: 'flex', flexDirection: 'column', padding: '15px', marginBottom: '20px', minHeight: '140px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 'auto' }}>
                <span style={{ fontSize: '11px', fontWeight: 'bold', color: tStyle.subtext, textTransform: 'uppercase' }}>Speedometer</span>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: wpm >= targetWpm ? '#10b981' : '#38bdf8' }}>{wpm} WPM</span>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '100%', gap: '4px', marginTop: '15px' }}>
                {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0].map((multiplier, idx) => {
                  const targetForThisBar = targetWpm * multiplier;
                  const prevTarget = targetWpm * (multiplier - 0.1);
                  
                  // Base structural height creates the nice staircase curve (15% up to 100%)
                  const baseHeight = 15 + (idx * 9.4); 
                  
                  // Smoothly calculate how full THIS specific bar should be so it rises slowly
                  let heightPct = 12; // Minimum 12% so empty bars are slightly visible
                  if (wpm >= targetForThisBar) {
                    heightPct = baseHeight;
                  } else if (wpm > prevTarget) {
                    // Fractional fill for the bar currently being worked on
                    const progress = (wpm - prevTarget) / (targetForThisBar - prevTarget);
                    heightPct = 12 + (progress * (baseHeight - 12));
                  }

                  // Make the top 2 bars green when lit (90% and 100% threshold)
                  const isLit = wpm > prevTarget; 
                  const barColor = isLit ? (idx >= 8 ? '#10b981' : '#38bdf8') : (isDarkMode ? '#334155' : '#e2e8f0');

                  return (
                    <div key={idx} style={{ 
                      flex: 1, 
                      maxWidth: '14px', 
                      backgroundColor: barColor, 
                      height: `${heightPct}%`, 
                      borderRadius: '4px', 
                      transition: 'height 0.4s ease-out, background-color 0.4s ease-out' 
                    }}></div>
                  )
                })}
              </div>
            </div>

            {/* 🚀 3. SMART EARLY EXIT BUTTON (Locked until goals are met!) */}
            <div style={{ marginTop: isMobile ? '0' : 'auto' }}>
              {(() => {
                // Check if they have met ALL requirements!
                const isEligible = wpm >= targetWpm && accuracy >= targetAcc && timeLeft <= 120;
                
                return (
                  <button 
                    disabled={!isEligible}
                    onClick={() => { 
                      setIsTimerRunning(false); 
                      // Because they are eligible, this counts as a TRUE success!
                      setIsManualExit(false); 
                      setAppState("results"); 
                    }} 
                    style={{ 
                      width: '100%', 
                      padding: '14px', 
                      backgroundColor: isEligible ? '#10b981' : 'transparent', 
                      color: isEligible ? 'white' : tStyle.subtext, 
                      border: isEligible ? 'none' : `2px dashed ${tStyle.border}`, 
                      borderRadius: '10px', 
                      cursor: isEligible ? 'pointer' : 'not-allowed', 
                      fontWeight: 'bold', 
                      fontSize: '14px',
                      transition: 'all 0.3s ease',
                      boxShadow: isEligible ? '0 4px 15px rgba(16, 185, 129, 0.4)' : 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isEligible ? "✅ Save & End Early" : "🔒 Goal Not Met"}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
        
        {/* 🚀 THE CORRECT APP.JSX INPUT (Now 100% Cheat-Proof) */}
        <input 
          type="text" 
          ref={inputRef} 
          onChange={handleTyping} 
          onKeyDown={handleKeyDown} 
          onKeyUp={handleKeyUp} 
          autoFocus 
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} 
          onPaste={(e) => e.preventDefault()} 
          onCopy={(e) => e.preventDefault()} 
          onDrop={(e) => e.preventDefault()} 
          autoComplete="off"
        />
      </div>
    );
  }

  if (appState === "results") {
    
    const tStyle = {
      bg: isDarkMode ? '#0f172a' : '#f1f5f9',
      box: isDarkMode ? '#1e293b' : '#ffffff', 
      text: isDarkMode ? '#f8fafc' : '#0f172a',
      subtext: isDarkMode ? '#94a3b8' : '#64748b',
      border: isDarkMode ? '#334155' : '#e2e8f0',
      statBg: isDarkMode ? '#0f172a' : '#f8fafc',
      success: isDarkMode ? '#4ade80' : '#16a34a',
      warning: isDarkMode ? '#fbbf24' : '#d97706',
      error: isDarkMode ? '#f87171' : '#dc2626',
      graphLine: isDarkMode ? '#475569' : '#cbd5e1',
      barColor: isDarkMode ? '#38bdf8' : '#60a5fa'
    };

    const targetWpm = isTypingTest ? 40 : currentGoal.wpm;
    const isSuccess = wpm >= targetWpm && accuracy >= currentGoal.accuracy && !isManualExit;
    const gradientColors = isManualExit ? '#06b6d4, #8b5cf6' : (isSuccess ? '#10b981, #3b82f6' : '#ef4444, #f59e0b');

    const totalTime = isTypingTest ? testDuration : (currentLevelData?.timeLimit || 300);
    const timeSpent = Math.max(1, totalTime - timeLeft);
    const timeString = `${Math.floor(timeSpent / 60)}:${String(timeSpent % 60).padStart(2, '0')} min.`;

    const displayGross = typeof grossWpm !== 'undefined' ? grossWpm : Math.round(wpm * (100 / Math.max(accuracy, 1)));

    let topMistakes = [];
    if (!isTypingTest) {
      const mistakeCounts = {};
      Object.values(mistakeHistory || {}).forEach(char => {
         mistakeCounts[char] = (mistakeCounts[char] || 0) + 1;
      });
      topMistakes = Object.entries(mistakeCounts).sort((a,b) => b[1] - a[1]).slice(0, 8);
    }
    const maxMistakes = topMistakes.length > 0 ? Math.max(topMistakes[0][1], 3) : 3;

    return (
      <div 
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') document.getElementById('btn-cancel')?.focus();
          if (e.key === 'ArrowRight') document.getElementById('btn-ok')?.focus();
        }}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%', backgroundColor: tStyle.bg, padding: isMobile ? '10px' : '20px', boxSizing: 'border-box', fontFamily: 'sans-serif', transition: 'background-color 0.3s' }}>
        
        <div 
          role="alert" 
          aria-live="assertive"
          style={{ 
          display: 'flex', flexDirection: 'column', width: '100%', 
          maxWidth: '700px', height: isMobile ? 'auto' : '550px', minHeight: '550px', 
          background: `linear-gradient(${tStyle.box}, ${tStyle.box}) padding-box, linear-gradient(135deg, ${gradientColors}) border-box`,
          border: '4px solid transparent', borderRadius: '16px', padding: isMobile ? '20px' : '35px 40px', 
          boxShadow: isDarkMode ? '0 15px 40px rgba(0,0,0,0.5)' : '0 15px 40px rgba(0,0,0,0.1)', 
          textAlign: 'left', transition: 'all 0.3s', boxSizing: 'border-box'
        }}>
          
          <h2 style={{ margin: '0 0 15px 0', color: isSuccess ? tStyle.success : (isManualExit ? tStyle.warning : tStyle.text), fontSize: '22px', borderBottom: `2px solid ${tStyle.border}`, paddingBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
             {isManualExit ? (text.drillEnded || "Interrupted") : (isSuccess ? (text.goalReached || "Completed") : (text.keepPracticing || "Keep Practicing"))}
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '25px', maxWidth: '550px' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              <span style={{ width: '120px', fontWeight: 'bold', color: tStyle.text }}>{text.timeUsed || "Time Used"}</span>
              <span style={{ width: '90px', color: tStyle.text }}>{timeString}</span>
              <span style={{ color: isManualExit ? tStyle.warning : tStyle.success, fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ backgroundColor: isManualExit ? tStyle.warning : tStyle.success, color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{isManualExit ? '!' : '✓'}</span>
                <span>{timeLeft > 0 ? `(${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')} remaining)` : "(Time's up!)"}</span>
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              <span style={{ width: '120px', fontWeight: 'bold', color: tStyle.text }}>{text.grossSpeed || "Gross Speed"}</span>
              <span style={{ width: '90px', color: tStyle.text }}>{displayGross} wpm</span>
              <span style={{ color: displayGross >= targetWpm ? tStyle.success : tStyle.warning, fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ backgroundColor: displayGross >= targetWpm ? tStyle.success : tStyle.warning, color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{displayGross >= targetWpm ? '✓' : '!'}</span>
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              <span style={{ width: '120px', fontWeight: 'bold', color: tStyle.text }}>{text.accuracy || "Accuracy"}</span>
              <span style={{ width: '90px', color: tStyle.text }}>{accuracy}%</span>
              <span style={{ color: accuracy >= currentGoal.accuracy ? tStyle.success : tStyle.warning, fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ backgroundColor: accuracy >= currentGoal.accuracy ? tStyle.success : tStyle.warning, color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{accuracy >= currentGoal.accuracy ? '✓' : '!'}</span>
                <span>(Target: {currentGoal.accuracy}%)</span>
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', fontSize: '14px' }}>
              <span style={{ width: '120px', fontWeight: 'bold', color: tStyle.text }}>{text.netSpeed || "Net Speed"}</span>
              <span style={{ width: '90px', color: tStyle.text }}>{wpm} wpm</span>
              <span style={{ color: wpm >= targetWpm ? tStyle.success : tStyle.warning, fontWeight: 'bold', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ backgroundColor: wpm >= targetWpm ? tStyle.success : tStyle.warning, color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' }}>{wpm >= targetWpm ? '✓' : '!'}</span>
                <span style={{ fontStyle: 'italic' }}>
                  {wpm >= targetWpm + 15 ? "🔥 Absolutely Unstoppable!" : 
                   wpm >= targetWpm + 5 ? "🚀 Fantastic Speed!" : 
                   wpm >= targetWpm ? "⭐ Goal Reached! Great Job!" : 
                   "💪 Keep pushing, you'll get there!"}
                </span>
              </span>
            </div>
          </div>

          {!isTypingTest && (
            <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontWeight: 'bold', color: tStyle.text, fontSize: '14px', marginBottom: '8px' }}>{text.difficultKeys || "Difficult Keys"}</div>
              
              {topMistakes.length > 0 ? (
                <div style={{ position: 'relative', flexGrow: 1, backgroundColor: tStyle.statBg, border: `1px solid ${tStyle.border}`, borderRadius: '8px', padding: '10px 10px 25px 10px', minHeight: '160px', overflow: 'hidden' }}>
                  
                  <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '34%', backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(254, 226, 226, 0.6)', borderBottom: `1px dashed ${isDarkMode ? '#7f1d1d' : '#fca5a5'}`, zIndex: 1 }}></div>
                  <div style={{ position: 'absolute', top: '6px', right: '10px', fontSize: '10px', color: isDarkMode ? '#fca5a5' : '#ef4444', fontWeight: 'bold', zIndex: 1, textTransform: 'uppercase' }}>Problematic</div>
                  
                  <div style={{ position: 'absolute', top: '34%', left: '0', width: '100%', height: '33%', backgroundColor: isDarkMode ? 'rgba(234, 179, 8, 0.08)' : 'rgba(254, 249, 195, 0.6)', borderBottom: `1px dashed ${isDarkMode ? '#713f12' : '#fde047'}`, zIndex: 1 }}></div>
                  <div style={{ position: 'absolute', top: '38%', right: '10px', fontSize: '10px', color: isDarkMode ? '#fde047' : '#eab308', fontWeight: 'bold', zIndex: 1, textTransform: 'uppercase' }}>Difficult</div>

                  <div style={{ position: 'absolute', bottom: '0', left: '0', width: '100%', height: '33%', backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.05)' : 'rgba(220, 252, 231, 0.4)', zIndex: 1 }}></div>
                  <div style={{ position: 'absolute', bottom: '30px', right: '10px', fontSize: '10px', color: isDarkMode ? '#86efac' : '#22c55e', fontWeight: 'bold', zIndex: 1, textTransform: 'uppercase' }}>OK</div>

                  <div style={{ position: 'absolute', bottom: '25px', left: '20px', right: '50px', top: '20px', display: 'flex', alignItems: 'flex-end', gap: '14px', zIndex: 2 }}>
                    {topMistakes.map(([char, count], i) => {
                       
                       let barHeightNum = 0;
                       let barColor = '#22c55e'; 
                       let barBorder = isDarkMode ? '#14532d' : '#166534';
                       
                       if (count <= 5) {
                         barHeightNum = Math.max(8, (count / 5) * 33); 
                       } else if (count <= 15) {
                         barHeightNum = 33 + ((count - 5) / 10) * 33; 
                         barColor = '#eab308'; 
                         barBorder = isDarkMode ? '#713f12' : '#854d0e'; 
                       } else {
                         barHeightNum = Math.min(100, 66 + ((count - 15) / 10) * 34); 
                         barColor = '#ef4444'; 
                         barBorder = isDarkMode ? '#7f1d1d' : '#991b1b'; 
                       }
                       
                       const barHeight = `${barHeightNum}%`;

                       return (
                         <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', width: '24px', justifyContent: 'flex-end' }}>
                            <div title={`${count} mistakes`} style={{ width: '100%', height: barHeight, backgroundColor: barColor, border: `1px solid ${barBorder}`, borderRadius: '4px 4px 0 0', transition: 'height 1s ease-out' }}></div>
                            <div style={{ position: 'absolute', bottom: '-22px', fontSize: '13px', fontWeight: 'bold', color: tStyle.text, textTransform: 'lowercase' }}>{char === 'SPACE' || char === ' ' ? 'spc' : char}</div>
                         </div>
                       )
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: tStyle.statBg, border: `1px solid ${tStyle.border}`, borderRadius: '8px', color: tStyle.success, fontWeight: 'bold', fontSize: '14px' }}>
                  {text.flawless || "Flawless!"}
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '15px', borderTop: `1px solid ${tStyle.border}` }}>
            <button 
              id="btn-cancel"
              onClick={() => {
                if (isSuccess && !isTypingTest) { saveProgressToDatabase(currentLevel, wpm, displayGross, accuracy); }
                setAppState("menu");
              }} 
              onFocus={(e) => e.target.style.outline = `3px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`}
              onBlur={(e) => e.target.style.outline = 'none'}
              style={{ padding: '8px 24px', backgroundColor: tStyle.statBg, color: tStyle.text, border: `1px solid ${tStyle.border}`, borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}>
              {text.btnCancel || "Cancel"}
            </button>
            <button 
              id="btn-ok"
              autoFocus 
              onClick={() => {
                if (isTypingTest) {
                    // For Demo Tests, hitting the button tries to start a new test.
                    // The guest trap inside startTypingTest will catch them here!
                    startTypingTest();
                } else if (isSuccess) {
                    saveProgressToDatabase(currentLevel, wpm, displayGross, accuracy);
                    // 🚀 FIX: Actually advance to the next drill instead of the menu!
                    openDrillInfo(currentLevel + 1); 
                } else {
                    openDrillInfo(currentLevel);
                }
              }} 
              onFocus={(e) => e.target.style.outline = `3px solid ${isDarkMode ? '#38bdf8' : '#0284c7'}`}
              onBlur={(e) => e.target.style.outline = 'none'}
              style={{ padding: '8px 30px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', boxShadow: '0 4px 6px rgba(22, 163, 74, 0.2)' }}>
              {isTypingTest ? "Try Again 🔄" : (isSuccess ? (text.btnNextDrill || "Next Drill ➡️") : (text.btnTryAgain || "Try Again 🔄"))}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default App;